import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import {
  safeGetItem,
  safeSetItem,
  safeGetJSON,
  safeSetJSON,
  safeRemoveItem,
} from "../../utils/storage";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-hot-toast";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Typography from "@mui/material/Typography";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Drawer from "@mui/material/Drawer";
import Avatar from "@mui/material/Avatar";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import {
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  InsertDriveFile as FileIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FileCopy as FileCopyIcon,
  DriveFileMove as DriveFileMoveIcon,
  HelpOutline as InfoIcon,
  CreateNewFolder as CreateNewFolderIcon,
  Home as HomeIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  Videocam as VideoIcon,
  AudioFile as AudioIcon,
  Close as CloseIcon,
  Description as DescriptionIcon,
  OpenInNew as OpenInNewIcon,
  Share as ShareIcon,
  Link as LinkIcon,
  CloudUpload as CloudUploadIcon,
  History as HistoryIcon,
  FolderShared as FolderSharedIcon,
  Download as DownloadIcon,
  AccountTree as TreeIcon,
  SubdirectoryArrowRight as SubfolderIcon,
} from "@mui/icons-material";
import CircularProgress from "@mui/material/CircularProgress";
import { FullscreenFileViewer } from "./FullscreenFileViewer";
import { ViewToggle } from "./ViewToggle";
import { FileThumbnail } from "./FileThumbnail";
import { DocumentViewer } from "./DocumentViewer";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import { useDispatch, useSelector } from "react-redux";
import {
  getAcceptedFileTypes,
  validateFileSize,
  isSupportedFileType,
  getFileExtension,
  getSupportedFileTypesDescription,
  getFileTypeForDatabase,
  formatFileSize,
} from "../../utils/fileTypes.ts";
import {
  fetchDirectories,
  createFolderThunk,
  updateDirectoryThunk,
  deleteDirectoryThunk,
  addDrawingThunk,
  clearCreateFolderError,
  clearUpdateDirectoryError,
  clearDeleteDirectoryError,
  setFiles,
  setSelectedIds as setSelectedIdsAction,
  setCurrentFolder as setCurrentFolderAction,
  setBreadcrumbs as setBreadcrumbsAction,
  toggleStar,
  setExpandedFolders as setExpandedFoldersAction,
  toggleStarThunk,
  setSelectedDrawingForView,
} from "../../store/slices/filesSlice";
import LoaderComponent from "../../components/LoaderComponent.js";
import {
  disableItem,
  getNamewithTrimmed,
  validationOnCharLength,
} from "../../utils/commonFunction.js";
import configrationData from "../../constants/configrationData.json";
import { DialogContentText } from "@mui/material";
import CreteNewButtonComponet from "../../components/myDrawingSection/creteNewButtonComponet.js";
import { normalizeDirectoryItem } from "../../utils/normalizeDirectoryItem.js";
import FavoritesButton from "../../components/myDrawingSection/favoritesButton.js";
import { EmptyDirectoryComponent } from "../../components/myDrawingSection/emptyDirectoryComponent.js";
import BulkSelectionToolbar from "../../components/myDrawingSection/BulkSelectionToolbar.js";
import ButtonComponet from "../../components/button/ButtonComponet.js";
import { TabSettingsComponent } from "./TabSettingsComponent.jsx";
import {
  fetchUserDisplaySettings,
  saveUserDisplaySettings,
} from "../../store/slices/userSlice";
import WarningIcon from "@mui/icons-material/Warning";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { tokenManager } from "../../utils/tokenManager.js";
import { get } from "../../services/api.js";

// Get file type icon
const getFileIcon = (node, isExpanded) => {
  if (node.type === "folder") {
    return isExpanded ? (
      <FolderOpenIcon fontSize="small" />
    ) : (
      <FolderIcon fontSize="small" />
    );
  }
  const fileType = node.fileType?.toLowerCase() || "";
  if (fileType.includes("pdf")) return <PdfIcon fontSize="small" />;
  if (
    fileType.includes("image") ||
    fileType.includes("png") ||
    fileType.includes("jpg")
  )
    return <ImageIcon fontSize="small" />;
  if (fileType.includes("video")) return <VideoIcon fontSize="small" />;
  if (fileType.includes("audio")) return <AudioIcon fontSize="small" />;
  return <FileIcon fontSize="small" />;
};
export function GoogleDriveFileList({
  files: initialFiles,
  onFileSelect,
  onFileOpen,
  onFilesChange,
  selectedDrawingForView,
  onClearSelectedDrawing,
}) {
  const dispatch = useDispatch();
  const {
    isCreatingFolder,
    createFolderError,
    fetchDirectoriesError,
    isUpdatingDirectory,
    updateDirectoryError,
    isDeletingDirectory,
    deleteDirectoryError,
    isLoadingDirectories,
    items: reduxFiles,
    selectedIds: reduxSelectedIds,
    currentFolder: reduxCurrentFolder,
    breadcrumbs: reduxBreadcrumbs,
    expandedFolders: reduxExpandedFolders,
  } = useSelector((state) => state.files);

  // Get current user for user-specific settings
  const authUser = useSelector((state) => state.auth.user);
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);

  // Get display settings from Redux for my-drawings tab
  const displaySettings = useSelector(
    (state) => state.user?.displayTabSettings?.["my-drawings"] || {}
  );

  // Get the sortable column from Redux - only this column will be sortable
  const sortableColumn = displaySettings?.defaultSortBy || "name";

  // Use Redux state as primary source, fallback to local state for UI-only features
  const files = initialFiles || reduxFiles || [];
  // Create selectedIds Set from Redux state - use useMemo to avoid recreating on every render
  const selectedIds = useMemo(() => {
    return reduxSelectedIds ? new Set(reduxSelectedIds) : new Set();
  }, [reduxSelectedIds]);
  const currentFolder = reduxCurrentFolder;
  const breadcrumbs = reduxBreadcrumbs || [];
  const expandedFoldersSet = reduxExpandedFolders
    ? new Set(reduxExpandedFolders)
    : new Set();

  // Helper function to get default sort direction for a field
  const getDefaultSortDirection = (field) => {
    const directionMap = {
      name: "asc",
      dateModified: "desc",
      size: "desc",
      type: "asc",
    };
    return directionMap[field] || "asc";
  };

  // Local state for UI-only features - initialize from display settings
  const defaultSortBy = displaySettings?.defaultSortBy || "name";
  const [sortField, setSortField] = useState(defaultSortBy);
  console.log("sortField-------------------------", sortField);

  const [sortDirection, setSortDirection] = useState(
    getDefaultSortDirection(defaultSortBy)
  );
  const [searchQuery, setSearchQuery] = useState("");

  // Sync sortField and sortDirection when display settings change
  useEffect(() => {
    if (displaySettings?.defaultSortBy) {
      const newSortField = displaySettings.defaultSortBy;
      setSortField(newSortField);
      setSortDirection(getDefaultSortDirection(newSortField));
    }
  }, [displaySettings?.defaultSortBy]);

  // Refs to track if errors were already handled (prevents duplicates in StrictMode)
  const handledErrorsRef = useRef({
    createFolderError: null,
    updateDirectoryError: null,
    deleteDirectoryError: null,
    fetchDirectoriesError: null,
  });

  const fecthData = async () => {
    setLoading(true);
    // Fetch directories from API
    await dispatch(fetchDirectories());
    setLoading(false);
  };
  // Fetch directories from API on mount
  useEffect(() => {
    fecthData();
  }, [dispatch]);

  // Fetch display settings on mount
  useEffect(() => {
    dispatch(fetchUserDisplaySettings("my-drawings"));
  }, [dispatch]);

  // Handle errors from Redux (only show errors, don't clear them here to avoid loops)
  // Use refs to prevent duplicate toasts in React StrictMode
  // Use same 'id' for react-hot-toast to prevent duplicates
  useEffect(() => {
    if (
      createFolderError &&
      handledErrorsRef.current.createFolderError !== createFolderError
    ) {
      handledErrorsRef.current.createFolderError = createFolderError;
      toast.error(createFolderError, { id: "createFolderError" });
      // Clear error after showing (with a small delay to avoid state update during render)
      setTimeout(() => {
        dispatch(clearCreateFolderError());
        // Reset ref after clearing to allow same error to be shown again if it reoccurs
        handledErrorsRef.current.createFolderError = null;
      }, 100);
    }
  }, [createFolderError, dispatch]);

  useEffect(() => {
    if (
      updateDirectoryError &&
      handledErrorsRef.current.updateDirectoryError !== updateDirectoryError
    ) {
      handledErrorsRef.current.updateDirectoryError = updateDirectoryError;
      toast.error(updateDirectoryError, { id: "updateDirectoryError" });
      setTimeout(() => {
        dispatch(clearUpdateDirectoryError());
        handledErrorsRef.current.updateDirectoryError = null;
      }, 100);
    }
  }, [updateDirectoryError, dispatch]);

  useEffect(() => {
    if (
      deleteDirectoryError &&
      handledErrorsRef.current.deleteDirectoryError !== deleteDirectoryError
    ) {
      handledErrorsRef.current.deleteDirectoryError = deleteDirectoryError;
      toast.error(deleteDirectoryError, { id: "deleteDirectoryError" });
      setTimeout(() => {
        dispatch(clearDeleteDirectoryError());
        handledErrorsRef.current.deleteDirectoryError = null;
      }, 100);
    }
  }, [deleteDirectoryError, dispatch]);

  useEffect(() => {
    if (
      fetchDirectoriesError &&
      handledErrorsRef.current.fetchDirectoriesError !== fetchDirectoriesError
    ) {
      handledErrorsRef.current.fetchDirectoriesError = fetchDirectoriesError;
      toast.error(fetchDirectoriesError, { id: "fetchDirectoriesError" });
      // Reset ref after a delay to allow same error to be shown again if it reoccurs
      setTimeout(() => {
        handledErrorsRef.current.fetchDirectoriesError = null;
      }, 100);
    }
  }, [fetchDirectoriesError]);

  // Check if selectedDrawingForView still exists in files list, clear if deleted
  useEffect(() => {
    if (selectedDrawingForView && files.length > 0) {
      // Helper function to recursively search for a file by ID
      const findFileById = (fileList, targetId) => {
        for (const file of fileList) {
          if (String(file.id) === String(targetId)) {
            return true;
          }
          if (file.children && Array.isArray(file.children)) {
            if (findFileById(file.children, targetId)) {
              return true;
            }
          }
        }
        return false;
      };

      const fileExists = findFileById(files, selectedDrawingForView.id);
      if (!fileExists) {
        // File was deleted, clear it
        localStorage.removeItem("selectedDrawingForView");
        dispatch(setSelectedDrawingForView(null));
        if (onClearSelectedDrawing) {
          onClearSelectedDrawing();
        }
      }
    }
  }, [files, selectedDrawingForView, dispatch, onClearSelectedDrawing]);

  // Google Drive Feature: Show selected file in breadcrumb
  // When user clicks a file, it appears in breadcrumb path (non-clickable)
  // Clear on: Esc key, click away, navigate folders, or click parent in breadcrumb
  const [selectedFileForBreadcrumb, setSelectedFileForBreadcrumb] =
    useState(null);

  // Use Redux expanded folders, sync with localStorage
  const expandedFolders = useMemo(() => {
    if (reduxExpandedFolders && reduxExpandedFolders.length > 0) {
      return new Set(reduxExpandedFolders);
    }
    // Fallback to localStorage - defensive: safely get expanded folders
    const saved = safeGetJSON("file-tree-expanded-folders", null);
    if (saved && Array.isArray(saved)) {
      return new Set(saved);
    }
    return new Set();
  }, [reduxExpandedFolders]);

  // Helper to update expanded folders (updates both Redux and localStorage)
  const updateExpandedFolders = useCallback(
    (newExpanded) => {
      const array = Array.from(newExpanded);
      dispatch(setExpandedFoldersAction(array));
      // Defensive: safely save expanded folders to storage
      safeSetJSON("file-tree-expanded-folders", array);
    },
    [dispatch]
  );

  // View mode state with localStorage persistence - use defaultView from settings as fallback
  const [viewMode, setViewMode] = useState();

  // Handler for view mode change
  const handleViewModeChange = useCallback((newMode) => {
    setViewMode(newMode);
  }, []);

  const [hoveredRowId, setHoveredRowId] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const menuJustClosedRef = useRef(false);
  const [showAddFolderDialog, setShowAddFolderDialog] = useState(false);
  const [showAddDrawingDialog, setShowAddDrawingDialog] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [newDrawingName, setNewDrawingName] = useState("");
  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [renameItem, setRenameItem] = useState(null);
  const [renameName, setRenameName] = useState("");
  const [showDocumentViewer, setShowDocumentViewer] = useState(false);
  const [viewerDocument, setViewerDocument] = useState(null);
  const [targetFolder, setTargetFolder] = useState(null);
  const [showInfoPanel, setShowInfoPanel] = useState(false);
  const [infoFile, setInfoFile] = useState(null);
  const [infoTabValue, setInfoTabValue] = useState(0);
  const [fileDescription, setFileDescription] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [fullscreenFile, setFullscreenFile] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState(false);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);

  const [activities, setActivities] = useState([]);
  const [isLoadingActivities, setIsLoadingActivities] = useState(false);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({
    open: false,
    file: null,
    associatedData: null,
    isChecking: false,
  });
  // Defensive: safely get user from storage
  const user = safeGetJSON("auth_user", null);

  // Ref for table container to detect clicks outside
  const tableContainerRef = useRef(null);
  // Ref to track if we've already attempted to restore from localStorage
  const hasRestoredFromStorageRef = useRef(false);

  // Restore selectedIds from localStorage on mount and when files are first loaded
  useEffect(() => {
    // Only restore once, when files are first loaded and we haven't restored yet
    // Skip if we've already restored or if Redux has selections
    if (hasRestoredFromStorageRef.current) {
      return; // Already attempted restoration
    }

    // Skip if Redux already has selections (user has made selections)
    if (reduxSelectedIds && reduxSelectedIds.length > 0) {
      hasRestoredFromStorageRef.current = true; // Mark as attempted
      return;
    }

    // Only restore if files are loaded
    if (!files || files.length === 0) {
      return; // Wait for files to load
    }

    // Defensive: safely get selected IDs from storage
    const saved = safeGetJSON("file-list-selected-ids", null);
    if (saved && Array.isArray(saved) && saved.length > 0) {
      const savedIds = saved;
      // Validate IDs against current files
      const validateIds = (nodes, validIds = new Set()) => {
        nodes.forEach((node) => {
          validIds.add(String(node.id));
          if (node.children && Array.isArray(node.children)) {
            validateIds(node.children, validIds);
          }
        });
        return validIds;
      };

      const validIds = validateIds(files);
      const filteredIds = savedIds.filter((id) => validIds.has(String(id)));

      if (filteredIds.length > 0) {
        dispatch(setSelectedIdsAction(filteredIds));
      } else if (savedIds.length > 0) {
        // Clear invalid IDs from storage
        safeRemoveItem("file-list-selected-ids");
      }
    }

    // Mark as attempted (even if nothing was restored)
    hasRestoredFromStorageRef.current = true;
  }, [files, reduxSelectedIds, dispatch]); // Run when files are loaded or Redux state changes

  // Persist selectedIds to localStorage whenever it changes
  useEffect(() => {
    try {
      if (selectedIds.size > 0) {
        localStorage.setItem(
          "file-list-selected-ids",
          JSON.stringify(Array.from(selectedIds))
        );
      } else {
        localStorage.removeItem("file-list-selected-ids");
      }
    } catch (error) {
      console.error("Error saving selected IDs to localStorage:", error);
    }
  }, [selectedIds]);

  // Sync Redux files with parent component (only when files actually change)
  useEffect(() => {
    if (onFilesChange) {
      // Use Redux files as primary source, fallback to initialFiles
      const filesToSync =
        reduxFiles && reduxFiles.length > 0 ? reduxFiles : initialFiles || [];
      onFilesChange(filesToSync);
    }
    // Only include reduxFiles and initialFiles in dependencies, not onFilesChange
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduxFiles, initialFiles]);

  // Sync currentFolder when files tree updates (safety measure - Redux reducers also handle this)
  // This ensures currentFolder references are fresh even if files are updated outside Redux actions
  useEffect(() => {
    if (reduxCurrentFolder && reduxCurrentFolder.id && files.length > 0) {
      // Find the current folder in the updated tree
      const freshCurrentFolder = findFileByIdInTree(
        files,
        reduxCurrentFolder.id
      );
      if (freshCurrentFolder) {
        // Only update if the reference is different (to avoid unnecessary re-renders)
        if (freshCurrentFolder !== reduxCurrentFolder) {
          dispatch(setCurrentFolderAction(freshCurrentFolder));
        }
      } else {
        // If currentFolder was deleted, reset to root
        dispatch(setCurrentFolderAction(null));
        dispatch(setBreadcrumbsAction([]));
      }
    }
    // Sync breadcrumbs as well
    if (reduxBreadcrumbs && reduxBreadcrumbs.length > 0 && files.length > 0) {
      const freshBreadcrumbs = [];
      for (const breadcrumb of reduxBreadcrumbs) {
        if (breadcrumb && breadcrumb.id) {
          const freshBreadcrumb = findFileByIdInTree(files, breadcrumb.id);
          if (freshBreadcrumb) {
            freshBreadcrumbs.push(freshBreadcrumb);
          } else {
            // If a breadcrumb folder was deleted, stop here
            break;
          }
        }
      }
      // Only update if breadcrumbs changed
      if (
        freshBreadcrumbs.length !== reduxBreadcrumbs.length ||
        freshBreadcrumbs.some((crumb, idx) => crumb !== reduxBreadcrumbs[idx])
      ) {
        dispatch(setBreadcrumbsAction(freshBreadcrumbs));
        // Update currentFolder to the last valid breadcrumb if breadcrumbs were truncated
        if (freshBreadcrumbs.length > 0) {
          dispatch(
            setCurrentFolderAction(
              freshBreadcrumbs[freshBreadcrumbs.length - 1]
            )
          );
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files, reduxCurrentFolder, reduxBreadcrumbs, dispatch]);

  // Sync Redux expanded folders to localStorage when they change - defensive: safely save expanded folders
  useEffect(() => {
    if (reduxExpandedFolders && reduxExpandedFolders.length > 0) {
      safeSetJSON("file-tree-expanded-folders", reduxExpandedFolders);
    }
  }, [reduxExpandedFolders]);

  // Keyboard shortcuts for tree navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl/Cmd + Shift + E: Expand all folders
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === "E") {
        e.preventDefault();
        const allFolderIds = new Set();
        const getAllFolderIds = (nodes) => {
          nodes.forEach((node) => {
            if (node.type === "folder") {
              allFolderIds.add(node.id);
              if (node.children) {
                getAllFolderIds(node.children);
              }
            }
          });
        };
        const currentFiles = currentFolder
          ? currentFolder.children || []
          : files;
        getAllFolderIds(currentFiles);
        updateExpandedFolders(allFolderIds);
        toast.success("All folders expanded (Ctrl+Shift+E)");
      }

      // Ctrl/Cmd + Shift + C: Collapse all folders
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === "C") {
        e.preventDefault();
        updateExpandedFolders(new Set());
        toast.success("All folders collapsed (Ctrl+Shift+C)");
      }

      // Ctrl/Cmd + Shift + B: Go back to parent folder
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === "B") {
        e.preventDefault();
        if (breadcrumbs.length > 0) {
          handleBreadcrumbClick(breadcrumbs.length - 2);
          toast.success("Navigated to parent folder");
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [files, currentFolder, breadcrumbs, updateExpandedFolders]);

  // Calculate folder size recursively
  const calculateFolderSize = (folder) => {
    if (folder.type === "file") {
      const size = folder.size;

      // Handle null/undefined
      if (!size) return 0;

      // If size is already a number (in bytes), convert to MB
      if (typeof size === "number") {
        return size / (1024 * 1024); // Convert bytes to MB
      }

      // If size is a string like "10 MB" or "1.5 GB"
      if (typeof size === "string") {
        const sizeStr = size || "0 MB";
        const sizeNum = parseFloat(sizeStr.replace(/[^0-9.]/g, ""));

        // Check if it's GB and convert to MB
        if (sizeStr.toLowerCase().includes("gb")) {
          return sizeNum * 1024; // Convert GB to MB
        }

        return sizeNum || 0;
      }

      return 0;
    }
    let totalSize = 0;
    if (folder.children) {
      for (const child of folder.children) {
        totalSize += calculateFolderSize(child);
      }
    }
    return totalSize;
  };
  const formatFolderSize = (folder) => {
    const totalMB = calculateFolderSize(folder);
    if (totalMB >= 1024) {
      return `${(totalMB / 1024).toFixed(2)} GB`;
    } else {
      return `${totalMB.toFixed(2)} MB`;
    }
  };
  const getFolderItemCount = (folder) => {
    return folder.children?.length || 0;
  };

  // Format date as DDMMMYY
  const formatDate = (dateString) => {
    if (!dateString || dateString === "Today") {
      const today = new Date();
      const day = String(today.getDate()).padStart(2, "0");
      const month = today.toLocaleString("en", {
        month: "short",
      });
      const year = String(today.getFullYear()).slice(-2);
      return `${day}${month}${year}`;
    }
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, "0");
      const month = date.toLocaleString("en", {
        month: "short",
      });
      const year = String(date.getFullYear()).slice(-2);
      return `${day}${month}${year}`;
    } catch {
      return dateString;
    }
  };

  // Get all children IDs recursively
  const getAllChildrenIds = (node) => {
    let ids = [node.id];
    if (node.children) {
      for (const child of node.children) {
        ids = ids.concat(getAllChildrenIds(child));
      }
    }
    return ids;
  };

  // Flatten files for tree view with indentation
  const flattenFilesWithDepth = (nodes, depth = 0, parentId) => {
    let result = [];
    for (const node of nodes) {
      result.push({
        ...node,
        depth,
        parentId,
      });
      if (
        node.type === "folder" &&
        node.children &&
        expandedFolders.has(node.id)
      ) {
        result = result.concat(
          flattenFilesWithDepth(node.children, depth + 1, node.id)
        );
      }
    }
    return result;
  };

  // Calculate maximum depth in the tree
  const calculateMaxDepth = (nodes, currentDepth = 0) => {
    let maxDepth = currentDepth;
    for (const node of nodes) {
      if (node.type === "folder" && node.children && node.children.length > 0) {
        const childDepth = calculateMaxDepth(node.children, currentDepth + 1);
        maxDepth = Math.max(maxDepth, childDepth);
      }
    }
    return maxDepth;
  };

  // Get current max depth for display
  const currentMaxDepth = useMemo(() => {
    const currentFiles = currentFolder ? currentFolder.children || [] : files;
    return calculateMaxDepth(currentFiles);
  }, [files, currentFolder]);

  // Sort files and folders together based on sort field
  const sortFiles = (filesToSort) => {
    return [...filesToSort].sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case "name":
          comparison = a.name.localeCompare(b.name);
          break;
        case "type":
          comparison = a.type.localeCompare(b.type);
          break;
        case "owner":
          comparison = (a.owner || "").localeCompare(b.owner || "");
          break;
        case "dateModified":
          comparison = (a.dateModified || "").localeCompare(
            b.dateModified || ""
          );
          break;
        case "size":
          // Handle size comparison for both files and folders together
          const getSizeInMB = (item) => {
            if (item.type === "folder") {
              return calculateFolderSize(item) / (1024 * 1024); // Convert bytes to MB
            }
            // Handle file size as number (bytes) or string (e.g., "10 MB")
            if (!item.size) return 0;
            if (typeof item.size === "number") {
              return item.size / (1024 * 1024); // Convert bytes to MB
            }
            if (typeof item.size === "string") {
              const sizeNum = parseFloat(
                item.size.replace(/[^0-9.]/g, "") || "0"
              );
              if (item.size.toLowerCase().includes("gb")) {
                return sizeNum * 1024; // Convert GB to MB
              }
              return sizeNum || 0;
            }
            return 0;
          };
          const sizeA = getSizeInMB(a);
          const sizeB = getSizeInMB(b);
          comparison = sizeA - sizeB;
          break;
      }
      return sortDirection === "asc" ? comparison : -comparison;
    });
  };

  // Filter files by search query
  const filterFiles = (nodes, query) => {
    if (!query) return nodes;
    const lowerQuery = query.toLowerCase();
    const filterRecursive = (node) => {
      const matches = node.name.toLowerCase().includes(lowerQuery);
      let filteredChildren;
      if (node.children) {
        filteredChildren = node.children
          .map((child) => filterRecursive(child))
          .filter((child) => child !== null);
      }
      if (matches || (filteredChildren && filteredChildren.length > 0)) {
        return {
          ...node,
          children: filteredChildren,
        };
      }
      return null;
    };
    return nodes
      .map((node) => filterRecursive(node))
      .filter((node) => node !== null);
  };

  // Auto-expand folders when searching
  useEffect(() => {
    if (searchQuery) {
      const currentFiles = currentFolder ? currentFolder.children || [] : files;
      const expandFolders = (nodes) => {
        const foldersToExpand = new Set();
        const findMatches = (node) => {
          const matches = node.name
            .toLowerCase()
            .includes(searchQuery.toLowerCase());
          let hasMatchingChild = false;
          if (node.children) {
            for (const child of node.children) {
              if (findMatches(child)) {
                hasMatchingChild = true;
                if (node.type === "folder") {
                  foldersToExpand.add(node.id);
                }
              }
            }
          }
          return matches || hasMatchingChild;
        };
        for (const node of nodes) {
          findMatches(node);
        }

        // Only update if there are new folders to expand
        if (foldersToExpand.size > 0) {
          // Get current expanded folders from Redux state (not from the memoized value to avoid loop)
          const currentExpanded = reduxExpandedFolders
            ? new Set(reduxExpandedFolders)
            : new Set();
          const newExpanded = new Set(currentExpanded);
          let hasNewFolders = false;

          foldersToExpand.forEach((id) => {
            if (!newExpanded.has(id)) {
              newExpanded.add(id);
              hasNewFolders = true;
            }
          });

          // Only update if there are actually new folders to expand
          if (hasNewFolders) {
            updateExpandedFolders(newExpanded);
          }
        }
      };
      expandFolders(currentFiles);
    }
    // Remove expandedFolders and updateExpandedFolders from dependencies to avoid infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, currentFolder, files, reduxExpandedFolders]);

  // Get processed files for display
  const displayedFiles = useMemo(() => {
    const currentFiles = currentFolder ? currentFolder.children || [] : files;
    const filtered = filterFiles(currentFiles, searchQuery);
    const sorted = sortFiles(filtered);
    return flattenFilesWithDepth(sorted);
  }, [
    files,
    currentFolder,
    searchQuery,
    sortField,
    sortDirection,
    expandedFolders,
  ]);

  // Get all child file IDs from a parent (for hover effect)
  const getChildFileIds = useCallback(
    (parentId) => {
      if (!parentId) return new Set();
      return new Set(
        displayedFiles
          .filter((file) => file.parentId === parentId)
          .map((file) => file.id)
      );
    },
    [displayedFiles]
  );

  // Handle folder expansion/collapse
  const toggleFolder = (folderId, event) => {
    event?.stopPropagation();
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId);
    } else {
      newExpanded.add(folderId);
    }
    updateExpandedFolders(newExpanded);
  };

  // Handle row selection with Shift+Click and Ctrl+Click support
  const lastSelectedIndexRef = useRef(-1);
  const handleSelectRow = (node, index, event) => {
    const newSelected = new Set(selectedIds);
    const isCurrentlySelected = selectedIds.has(node.id);
    if (event.shiftKey && lastSelectedIndexRef.current !== -1) {
      // Shift+Click: Select range (only the items themselves, not their children)
      const start = Math.min(lastSelectedIndexRef.current, index);
      const end = Math.max(lastSelectedIndexRef.current, index);
      for (let i = start; i <= end; i++) {
        if (displayedFiles[i]) {
          newSelected.add(displayedFiles[i].id);
        }
      }
    } else if (event.ctrlKey || event.metaKey) {
      // Ctrl+Click: Toggle individual selection (only the item itself, not its children)
      if (isCurrentlySelected) {
        // Remove only this node (not its children)
        newSelected.delete(node.id);
      } else {
        // Add only this node (not its children)
        newSelected.add(node.id);
      }
    } else {
      // Regular click: Clear all and select only this node (not its children)
      newSelected.clear();
      newSelected.add(node.id);
    }
    lastSelectedIndexRef.current = index;
    // Update Redux state
    dispatch(setSelectedIdsAction(Array.from(newSelected)));
  };

  // Helper function to find file by ID in the full tree
  const findFileByIdInTree = (nodes, id) => {
    for (const node of nodes) {
      if (node.id === id) return node;
      if (node.children) {
        const found = findFileByIdInTree(node.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  // Handle Select All checkbox
  const handleSelectAll = useCallback(
    (event) => {
      event.stopPropagation();
      // Get current selectedIds from Redux to ensure we have the latest state
      const currentSelectedIds = reduxSelectedIds
        ? new Set(reduxSelectedIds)
        : new Set();
      const allVisibleIds = displayedFiles.map((f) => f.id);

      if (allVisibleIds.length === 0) {
        return; // No items to select
      }

      // Check if all visible items are currently selected
      const allSelected = allVisibleIds.every((id) =>
        currentSelectedIds.has(id)
      );

      // Get the full files tree (not flattened) to access all children
      const currentFiles = currentFolder ? currentFolder.children || [] : files;

      const newSelected = new Set(currentSelectedIds);
      if (allSelected) {
        // Deselect all visible items (only the items themselves, not their children)
        displayedFiles.forEach((displayedFile) => {
          newSelected.delete(displayedFile.id);
        });
      } else {
        // Select all visible items (only the items themselves, not their children)
        displayedFiles.forEach((displayedFile) => {
          newSelected.add(displayedFile.id);
        });
      }

      // Update Redux state
      dispatch(setSelectedIdsAction(Array.from(newSelected)));
    },
    [reduxSelectedIds, displayedFiles, dispatch, files, currentFolder]
  );

  // Compute Select All checkbox state
  const allVisibleIds = useMemo(
    () => displayedFiles.map((f) => f.id),
    [displayedFiles]
  );
  const isAllSelected = useMemo(() => {
    if (allVisibleIds.length === 0) return false;
    // Use reduxSelectedIds directly to ensure we have the latest state
    const currentSelectedSet = reduxSelectedIds
      ? new Set(reduxSelectedIds)
      : new Set();
    return allVisibleIds.every((id) => currentSelectedSet.has(id));
  }, [allVisibleIds, reduxSelectedIds]);
  const isIndeterminate = useMemo(() => {
    if (allVisibleIds.length === 0) return false;
    // Use reduxSelectedIds directly to ensure we have the latest state
    const currentSelectedSet = reduxSelectedIds
      ? new Set(reduxSelectedIds)
      : new Set();
    const someSelected = allVisibleIds.some((id) => currentSelectedSet.has(id));
    return someSelected && !isAllSelected;
  }, [allVisibleIds, reduxSelectedIds, isAllSelected]);

  // Handle row click (select row only)
  const handleRowClick = (node, index, event) => {
    console.log(node, "node", index, "index", event, "event");
    // Material Design 3: Smart context-aware interaction
    // Any click = select row (no viewer opening)
    // Right-click = context menu with "File Preview" option
    event.stopPropagation();
    handleSelectRow(node, index, event);

    // Call onFileSelect to update global selection state
    if (node.type === "file" && onFileSelect) {
      onFileSelect(node);
    }
  };

  // Handle file name click (open fullscreen viewer)
  const handleFileNameClick = (node, event) => {
    event.stopPropagation();
    event.preventDefault(); // Prevent default download behavior

    if (node.type === "file" && node.fileUrl) {
      // Material Design 3: Single-click on file name opens appropriate viewer
      const extension = getFileExtension(node.name);

      // Check if file type is supported
      if (!isSupportedFileType(node.name)) {
        toast(`Preview not available for ${extension.toUpperCase()} files`, {
          icon: "ℹ️",
        });
        return;
      }

      // For PDF, DWG, and images, use the existing fullscreen viewer
      const isPDF = extension === "pdf";
      const isDWG = ["dwg", "dxf"].includes(extension);
      const isImage = ["jpg", "jpeg", "png"].includes(extension);
      if (isPDF || isDWG || isImage) {
        setFullscreenFile(node);
        if (onFileSelect) {
          onFileSelect(node);
        }
      } else {
        // For all other supported types (videos, Office docs, text files), use DocumentViewer
        setViewerDocument({
          fileName: node.name,
          fileUrl: node.fileUrl,
          fileType: extension,
        });
        setShowDocumentViewer(true);
        if (onFileSelect) {
          onFileSelect(node);
        }
      }
    }
  };

  // Handle double click (navigate into folder or open file in fullscreen)
  const handleRowDoubleClick = (node) => {
    if (node.type === "folder") {
      // Clear selections of all child items when navigating into a folder
      // Children should only be selected when manually selected, not inherited from parent view
      const currentSelectedIds = reduxSelectedIds
        ? new Set(reduxSelectedIds)
        : new Set();
      const newSelected = new Set(currentSelectedIds);

      // Always search from root files tree to get complete children structure
      // This ensures we find all nested children even when navigating from inside a folder
      const fullNode = findFileByIdInTree(files, node.id);
      const nodeToUse = fullNode || node;

      // Get all children IDs of the folder being navigated into (recursively)
      // getAllChildrenIds includes the node itself, so this will get folder + all children
      const allChildrenIds = getAllChildrenIds(nodeToUse);

      // Remove all child item selections (including the folder itself)
      allChildrenIds.forEach((id) => newSelected.delete(id));

      // Update Redux state with cleared child selections BEFORE navigating
      dispatch(setSelectedIdsAction(Array.from(newSelected)));

      // Update Redux state
      dispatch(setCurrentFolderAction(node));
      dispatch(setBreadcrumbsAction([...breadcrumbs, node]));
      if (onFileOpen) {
        onFileOpen(node);
      }
    } else if (node.type === "file" && node.fileUrl) {
      // Material Design 3: Double-click on file opens appropriate viewer
      const extension = getFileExtension(node.name);

      // Check if file type is supported
      if (!isSupportedFileType(node.name)) {
        toast(`Preview not available for ${extension.toUpperCase()} files`);
        return;
      }

      // For PDF, DWG, and images, use the existing fullscreen viewer
      const isPDF = extension === "pdf";
      const isDWG = ["dwg", "dxf"].includes(extension);
      const isImage = ["jpg", "jpeg", "png"].includes(extension);
      if (isPDF || isDWG || isImage) {
        setFullscreenFile(node);
        if (onFileSelect) {
          onFileSelect(node);
        }
      } else {
        // For all other supported types (videos, Office docs, text files), use DocumentViewer
        setViewerDocument({
          fileName: node.name,
          fileUrl: node.fileUrl,
          fileType: extension,
        });
        setShowDocumentViewer(true);
        if (onFileSelect) {
          onFileSelect(node);
        }
      }
    }
  };

  // Handle breadcrumb navigation
  const handleBreadcrumbClick = (index) => {
    const currentSelectedIds = reduxSelectedIds
      ? new Set(reduxSelectedIds)
      : new Set();
    const newSelected = new Set(currentSelectedIds);

    if (index === -1) {
      // Navigate to root - clear all selections of items that are not at root level
      const rootFileIds = new Set(files.map((f) => f.id));
      // Keep only root level selections
      const filteredSelected = Array.from(newSelected).filter((id) =>
        rootFileIds.has(id)
      );
      dispatch(setSelectedIdsAction(filteredSelected));
      dispatch(setCurrentFolderAction(null));
      dispatch(setBreadcrumbsAction([]));
    } else {
      // Navigate to specific folder - clear selections of items inside that folder
      const targetFolder = breadcrumbs[index];

      // Get all children IDs of the target folder
      const allChildrenIds = getAllChildrenIds(targetFolder);

      // Remove all child item selections
      allChildrenIds.forEach((id) => newSelected.delete(id));

      // Also remove the folder itself from selection if it was selected
      newSelected.delete(targetFolder.id);

      // Update Redux state with cleared child selections
      dispatch(setSelectedIdsAction(Array.from(newSelected)));

      dispatch(setCurrentFolderAction(targetFolder));
      dispatch(setBreadcrumbsAction(breadcrumbs.slice(0, index + 1)));
    }
  };

  // Handle sort - only allow sorting on the default sortable column from Redux
  const handleSort = (field) => {
    // Only allow sorting if the field matches the sortable column from Redux
    if (field !== sortableColumn) {
      return;
    }
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Handle three-dot menu
  const handleMenuClick = (event, file) => {
    event.stopPropagation();
    event.preventDefault();
    const buttonElement = event.currentTarget;
    if (buttonElement) {
      // Calculate button position for accurate menu placement
      const rect = buttonElement.getBoundingClientRect();
      setAnchorEl({
        element: buttonElement,
        position: {
          top: rect.bottom + 4, // Position below the button
          left: rect.left, // Align with left edge of button
        },
        file,
      });
    } else {
      // Fallback to element-only approach
      setAnchorEl({
        element: event.currentTarget,
        file,
      });
    }
  };
  const handleMenuClose = (event, reason) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    // Close context menu if it's open when closing the three-dot menu
    if (contextMenu) {
      setContextMenu(null);
    }
    setAnchorEl(null);
    // Set flag to prevent context menu from opening immediately after menu close
    menuJustClosedRef.current = true;
    setTimeout(() => {
      menuJustClosedRef.current = false;
    }, 300);
  };

  // Handle context menu
  const handleContextMenu = (event, file) => {
    event.preventDefault();
    event.stopPropagation();
    // Don't open context menu if three-dot menu is already open
    if (anchorEl) {
      return;
    }
    // Don't open context menu if a menu was just closed (prevents unwanted menu appearance)
    if (menuJustClosedRef.current) {
      return;
    }
    // Only open context menu if it's a genuine right-click event
    // Check if event has valid coordinates (not 0,0 which indicates synthetic event)
    const mouseX = event.clientX || 0;
    const mouseY = event.clientY || 0;
    if (mouseX === 0 && mouseY === 0) {
      return;
    }
    setContextMenu({
      mouseX: mouseX - 2,
      mouseY: mouseY - 4,
      file,
    });
  };
  const handleContextMenuClose = (event) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    setContextMenu(null);
    // Set flag to prevent context menu from opening immediately after menu close
    menuJustClosedRef.current = true;
    setTimeout(() => {
      menuJustClosedRef.current = false;
    }, 300);
  };

  // Handle star toggle
  const handleStarToggle = (file, event) => {
    event?.stopPropagation();

    // Validate file and id before proceeding
    if (!file || !file.id) {
      console.error("handleStarToggle: file or file.id is missing", file);
      toast.error("Cannot toggle star: Invalid file");
      return;
    }

    // Use Redux action for star toggle
    dispatch(toggleStarThunk({ id: file.id, starred: file.starred }));
  };

  // Check for associated data (sessions, pins, recordings)
  const checkAssociatedData = async (fileId) => {
    try {
      // Only check for files, not folders
      // Fetch sessions for this file/drawing
      const sessionsResponse = await get(`/sessions/${fileId}`);
      const sessions = sessionsResponse?.data || [];

      let totalPins = 0;
      let totalRecordings = 0;

      // For each session, fetch pins and count recordings
      for (const session of sessions) {
        try {
          const pinsResponse = await get(`/pins/${session.id}`);
          const pins = pinsResponse?.data || [];
          totalPins += pins.length;

          // Count recordings from all pins
          pins.forEach((pin) => {
            if (
              pin.construction_recordings &&
              Array.isArray(pin.construction_recordings)
            ) {
              totalRecordings += pin.construction_recordings.length;
            }
          });
        } catch (err) {
          console.error(`Error fetching pins for session ${session.id}:`, err);
        }
      }

      return {
        sessionsCount: sessions.length,
        pinsCount: totalPins,
        recordingsCount: totalRecordings,
      };
    } catch (error) {
      console.error("Error checking associated data:", error);
      // If error, assume no associated data to be safe
      return {
        sessionsCount: 0,
        pinsCount: 0,
        recordingsCount: 0,
      };
    }
  };

  // Fetch activity data for a directory/file
  const fetchActivities = async (directoryId) => {
    if (!directoryId) {
      setActivities([]);
      return;
    }

    setIsLoadingActivities(true);
    try {
      const response = await get(`/directory/${directoryId}/activity`);
      console.log("Activity API response:", response);
      // Handle response structure: response.data.activities
      const activitiesData =
        response?.data?.activities ||
        response?.activities ||
        response?.data ||
        [];
      console.log("Extracted activities:", activitiesData);
      setActivities(Array.isArray(activitiesData) ? activitiesData : []);
    } catch (error) {
      console.error("Error fetching activities:", error);
      setActivities([]);
      // Optionally show a toast error, but don't block the UI
      toast.error("Failed to load activity history");
    } finally {
      setIsLoadingActivities(false);
    }
  };

  // Handle delete - show confirmation dialog first
  const handleDelete = async (file) => {
    // Close menus immediately to prevent any event propagation
    handleMenuClose();
    handleContextMenuClose();

    // For folders: only show confirmation if folder is empty
    if (file.type === "folder") {
      const hasChildren = file.children && file.children.length > 0;
      if (hasChildren) {
        // Folder has contents, delete directly without confirmation
        await performDelete(file);
        return;
      }
      // Folder is empty, show confirmation dialog
      setDeleteConfirmDialog({
        open: true,
        file: file,
        associatedData: { sessionsCount: 0, pinsCount: 0, recordingsCount: 0 },
        isChecking: false,
      });
      return;
    }

    // For files: always show confirmation dialog and check for associated data
    setDeleteConfirmDialog({
      open: true,
      file: file,
      associatedData: null,
      isChecking: true,
    });

    const associatedData = await checkAssociatedData(file.id);
    setDeleteConfirmDialog({
      open: true,
      file: file,
      associatedData: associatedData,
      isChecking: false,
    });
  };

  // Actually perform the deletion (extracted to reusable function)
  const performDelete = async (file) => {
    if (!file) return;

    try {
      setLoading(true);
      console.log(file, "file", selectedFile, "selectedFile");
      const localStorageSelectedFile = safeGetJSON(
        "selectedDrawingForView",
        null
      );

      // Check if the deleted file is the active drawing (check both prop and localStorage)
      const isActiveDrawing =
        (selectedDrawingForView &&
          String(selectedDrawingForView.id) === String(file.id)) ||
        (localStorageSelectedFile &&
          String(localStorageSelectedFile.id) === String(file.id));

      if (file.type !== "folder" && isActiveDrawing) {
        setSelectedFile(null);
        localStorage.removeItem("selectedDrawingForView");
        // Clear selectedDrawingForView in Redux store
        dispatch(setSelectedDrawingForView(null));
        // Call parent callback to clear selectedDrawingForView in parent component
        if (onClearSelectedDrawing) {
          onClearSelectedDrawing();
        }
      }
      await dispatch(deleteDirectoryThunk(file.id)).unwrap();
      toast.success(
        `${file.type === "folder" ? "Folder" : "File"} "${
          file.name
        }" deleted successfully`
      );
      // Refresh directories after deletion
      dispatch(fetchDirectories());
      setLoading(false);
    } catch (error) {
      setLoading(false);
      // Error will be shown by useEffect watching deleteDirectoryError
      // Using same id prevents duplicate toast
      toast.error(
        error ||
          `Failed to delete ${file.type === "folder" ? "folder" : "file"}`,
        { id: "deleteDirectoryError" }
      );
    }
  };

  // Confirm delete from dialog
  const confirmDelete = async () => {
    const file = deleteConfirmDialog.file;
    if (!file) return;

    setDeleteConfirmDialog({
      open: false,
      file: null,
      associatedData: null,
      isChecking: false,
    });
    await performDelete(file);
  };

  // Cancel delete
  const cancelDelete = () => {
    setDeleteConfirmDialog({
      open: false,
      file: null,
      associatedData: null,
      isChecking: false,
    });
  };

  // Handle file preview from context menu
  const handleFilePreview = (file) => {
    if (file.type === "file") {
      const fileType = file.fileType?.toLowerCase() || "";
      const fileName = file.name.toLowerCase();

      // Check if file is PDF, DWG, or image
      const isPDF = fileType.includes("pdf") || fileName.endsWith(".pdf");
      const isDWG = fileType.includes("dwg") || fileName.endsWith(".dwg");
      const isImage =
        fileType.includes("image") ||
        fileName.endsWith(".png") ||
        fileName.endsWith(".jpg") ||
        fileName.endsWith(".jpeg");
      if (isPDF || isDWG || isImage) {
        setFullscreenFile(file);
        if (onFileSelect) {
          onFileSelect(file);
        }
        handleContextMenuClose();
        handleMenuClose();
      } else {
        toast(`Preview not available for ${file.fileType || "this file type"}`);
      }
    }
  };

  // Handle download
  const handleDownload = (file) => {
    // Close menus immediately
    handleMenuClose();
    handleContextMenuClose();
    if (file.type === "file" && file.fileUrl) {
      // Create a temporary anchor element to trigger download
      const link = document.createElement("a");
      link.href = file.fileUrl;
      link.download = file.name; // Set the filename for download
      link.target = "_blank"; // Open in new tab if download fails
      link.rel = "noopener noreferrer"; // Security best practice
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success(`Downloading ${file.name}`);
    } else {
      toast.error("Download not available for this file");
    }
  };

  // Helper function to find file by ID
  const findFileById = (nodes, id) => {
    for (const node of nodes) {
      if (node.id === id) return node;
      if (node.children) {
        const found = findFileById(node.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  // Handle add folder
  const handleAddFolder = async () => {
    if (!newFolderName.trim()) {
      toast.error("Please enter a folder name");
      return;
    }
    try {
      const parentId = currentFolder ? currentFolder.id : null;
      await dispatch(
        createFolderThunk({
          name: newFolderName,
          parent_id: parentId,
          type: "folder",
          owner: user?.name,
          owner_id: user?.id,
          dateModified: new Date().toLocaleDateString(),
          children: [],
        })
      ).unwrap();

      setShowAddFolderDialog(false);
      setNewFolderName("");
      toast.success(`Folder "${newFolderName}" created successfully`);
      // Refresh directories after creation
      dispatch(fetchDirectories());
    } catch (error) {
      // Error will be shown by useEffect watching createFolderError
      // Using same id prevents duplicate toast
      toast.error(error || "Failed to create folder", {
        id: "createFolderError",
      });
    }
  };

  // Handle add subfolder (from context menu)
  const handleAddSubfolder = (parentFolder) => {
    setTargetFolder(parentFolder);
    setShowAddFolderDialog(true);
    handleContextMenuClose();
    handleMenuClose();
  };

  // Handle add drawing (from context menu)
  const handleAddDrawing = (targetFolderNode) => {
    setTargetFolder(targetFolderNode || currentFolder);
    setShowAddDrawingDialog(true);
    handleContextMenuClose();
    handleMenuClose();
  };

  // Handle rename
  const handleRename = (file) => {
    setRenameItem(file);
    setRenameName(file.name);
    setShowRenameDialog(true);
    handleMenuClose();
    handleContextMenuClose();
  };

  // Handle rename submit
  const handleRenameSubmit = async () => {
    if (!renameName.trim()) {
      toast.error("Please enter a name");
      return;
    }
    if (!renameItem) {
      toast.error("No item selected for rename");
      return;
    }

    try {
      await dispatch(
        updateDirectoryThunk({
          id: renameItem.id,
          name: renameName.trim(),
        })
      ).unwrap();

      toast.success(
        `${
          renameItem.type === "folder" ? "Folder" : "File"
        } renamed successfully`
      );
      setShowRenameDialog(false);
      setRenameItem(null);
      setRenameName("");
      // Refresh directories after rename
      dispatch(fetchDirectories());
    } catch (error) {
      // Error will be shown by useEffect watching updateDirectoryError
      // Using same id prevents duplicate toast
      toast.error(
        error ||
          `Failed to rename ${
            renameItem.type === "folder" ? "folder" : "file"
          }`,
        { id: "updateDirectoryError" }
      );
    }
  };

  // Handle create subfolder in target folder
  const handleCreateSubfolder = async () => {
    if (!newFolderName.trim()) {
      toast.error("Please enter a folder name");
      return;
    }
    try {
      const parentId = targetFolder ? targetFolder.id : null;
      await dispatch(
        createFolderThunk({
          name: newFolderName,
          parent_id: parentId,
          type: "folder",
          owner: user?.name,
          owner_id: user?.id,
          dateModified: new Date().toLocaleDateString(),
          children: [],
        })
      ).unwrap();

      // Auto-expand the parent folder
      if (parentId) {
        const newExpanded = new Set(expandedFolders);
        newExpanded.add(parentId);
        updateExpandedFolders(newExpanded);
      }

      setShowAddFolderDialog(false);
      setNewFolderName("");
      setTargetFolder(null);
      toast.success(`Subfolder "${newFolderName}" created successfully`);
      // Refresh directories after creation
      dispatch(fetchDirectories());
    } catch (error) {
      // Error will be shown by useEffect watching createFolderError
      // Using same id prevents duplicate toast
      toast.error(error || "Failed to create subfolder", {
        id: "createFolderError",
      });
    }
  };

  // Helper function to convert file to base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  // Handle create drawing
  const handleCreateDrawing = async () => {
    if (!newDrawingName.trim()) {
      toast.error("Please enter a drawing name");
      return;
    }
    if (!selectedFile) {
      toast.error("Please select a file from your hard disk");
      return;
    }

    // Strict validation: Check file type before upload
    if (!isSupportedFileType(selectedFile.name)) {
      const supportedTypes = getSupportedFileTypesDescription();
      toast.error(
        `Unsupported file type. Only the following formats are allowed: ${supportedTypes}`
      );
      return;
    }

    // Validate file size
    const sizeValidation = validateFileSize(selectedFile.size);
    if (!sizeValidation.valid) {
      toast.error(sizeValidation.error);
      return;
    }

    // Get file extension from the selected file
    const fileExtension =
      selectedFile.name.split(".").pop()?.toLowerCase() || "pdf";
    const fileName = newDrawingName.includes(".")
      ? newDrawingName
      : `${newDrawingName}.${fileExtension}`;

    // Map file extension to database file_type enum value ('image', 'pdf', or 'application')
    const fileType = getFileTypeForDatabase(fileExtension);

    // Get MIME type
    const mimeType = selectedFile.type || `application/${fileExtension}`;

    try {
      // Convert file to base64
      const base64File = await fileToBase64(selectedFile);
      // Get parent ID
      const parentId = targetFolder
        ? targetFolder.id
        : currentFolder
        ? currentFolder.id
        : null;

      // Dispatch the upload thunk
      const result = await dispatch(
        addDrawingThunk({
          name: fileName.trim(),
          file: base64File, // Send as base64 data URL
          type: "file",
          file_type: fileType,
          mime_type: mimeType,
          size: selectedFile.size,
          owner: user?.name,
          owner_id: user?.id,
          dateModified: new Date().toLocaleDateString(),
          children: [],
          parent_id: parentId,
        })
      ).unwrap();

      // Auto-expand the parent folder if needed
      if (parentId) {
        const newExpanded = new Set(expandedFolders);
        newExpanded.add(parentId);
        updateExpandedFolders(newExpanded);
      }
      // Refresh directories to get the updated file with proper URL
      const response = await dispatch(fetchDirectories());
      // Recursive function to find file in nested children
      const findFileRecursively = (items, targetId) => {
        if (!items || !Array.isArray(items)) return null;
        for (const item of items) {
          if (item.id === targetId) {
            return item;
          }
          // Search in children recursively
          if (
            item.children &&
            Array.isArray(item.children) &&
            item.children.length > 0
          ) {
            const found = findFileRecursively(item.children, targetId);
            if (found) {
              return found;
            }
          }
        }
        return null;
      };

      const file = findFileRecursively(
        response.payload.data.directory,
        result.data.id
      );
      if (!file) {
        console.warn("File not found in directory structure:", result.data.id);
        return;
      }
      const node = normalizeDirectoryItem(file);
      // Call onFileSelect to update global selection state
      if (result.data.type === "file" && onFileSelect) {
        onFileSelect(node);
      }
      // Wait a bit for state to update, then select the new file
      // The file will be in the Redux state after fetchDirectories completes
      // We'll handle selection after the state updates
      setShowAddDrawingDialog(false);
      setNewDrawingName("");
      setSelectedFile(null);
      setTargetFolder(null);
      toast.success(`Drawing "${fileName}" uploaded successfully`);
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error(error || "Failed to upload file");
    }
  };

  const handleOnChangeFolderName = (value) => {
    if (!validationOnCharLength(value, configrationData.folder_length)) {
      setValidationError(true);
    } else {
      setValidationError(false);
      setNewFolderName(value);
    }
  };
  const handleOnChangeRenameName = (value, type) => {
    if (
      !validationOnCharLength(
        value,
        type === "folder"
          ? configrationData.folder_length
          : configrationData.file_length
      )
    ) {
      setValidationError(true);
    } else {
      setValidationError(false);
      setRenameName(value);
    }
  };
  const handleOnChangeDrawingName = (value) => {
    if (!validationOnCharLength(value, configrationData.file_length)) {
      setValidationError(true);
    } else {
      setValidationError(false);
    }
    setNewDrawingName(value);
  };

  const handleFavoritesClick = () => {
    const newShowFavoritesOnly = !showFavoritesOnly;
    setShowFavoritesOnly(newShowFavoritesOnly);
    // Fetch based on new state
    if (newShowFavoritesOnly) {
      dispatch(fetchDirectories({ starred: true }));
    } else {
      dispatch(fetchDirectories({ starred: false }));
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        height: "100%",
        gap: 0,
        position: "relative",
      }}
    >
      {isLoading && <LoaderComponent />}
      {/* Main File List */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* 🎯 INTEGRATED DUAL-ROW TOOLBAR + BREADCRUMBS (72px total - Compact WCAG AA) */}
        <Box
          sx={{
            bgcolor: "#F9FAFB",
            borderBottom: "1px solid #E5E7EB",
            display: "flex",
            flexDirection: "column",
            // Responsive: maintains dual-row layout, naturally stacks on overflow
          }}
        >
          {/* ═══════════════════════════════════════════════════════════ */}
          {/* ROW 1: BREADCRUMBS NAVIGATION (36px Height - Compact WCAG AA) */}
          {/* ═══════════════════════════════════════════════════════════ */}
          <Box
            sx={{
              px: 2.5,
              py: 0.75,
              display: "flex",
              alignItems: "center",
              minHeight: "36px",
            }}
          >
            <Tooltip
              title={
                // Full path tooltip for overflow cases
                breadcrumbs.length > 0
                  ? `My Drawings${breadcrumbs
                      .map((f) => ` › ${(f.name || "").trim()}`)
                      .join("")}`
                  : "My Drawings"
              }
              placement="bottom-start"
              arrow
            >
              <Breadcrumbs
                aria-label="breadcrumb"
                separator="›"
                sx={{
                  flex: 1,
                  overflow: "hidden",
                  "& .MuiBreadcrumbs-ol": {
                    flexWrap: "nowrap",
                  },
                  "& .MuiBreadcrumbs-separator": {
                    color: "#4A5565",
                    fontSize: "18px",
                    mx: 1,
                  },
                }}
              >
                {/* Home / Root Level - Always Clickable (unless we're at root) */}
                {breadcrumbs.length === 0 ? (
                  <Typography
                    variant="body2"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      color: "#212121",
                      fontWeight: 500,
                      fontFamily: "Roboto, sans-serif",
                      fontSize: "16px", // Increased from 14px (+2pt)
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "100%",
                    }}
                  >
                    <HomeIcon
                      sx={{
                        fontSize: 20,
                        flexShrink: 0,
                      }}
                    />
                    My Drawings
                  </Typography>
                ) : (
                  <Link
                    component="button"
                    variant="body2"
                    underline="hover"
                    onClick={() => handleBreadcrumbClick(-1)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      cursor: "pointer",
                      color: "#4A5565",
                      fontFamily: "Roboto, sans-serif",
                      fontSize: "16px",
                      // Increased from 14px (+2pt)
                      padding: "8px",
                      borderRadius: "4px",
                      transition: "all 0.2s",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "200px",
                      "&:hover": {
                        color: "#2932E5",
                        bgcolor: "rgba(41, 50, 229, 0.08)",
                      },
                    }}
                  >
                    <HomeIcon
                      sx={{
                        fontSize: 20,
                        flexShrink: 0,
                      }}
                    />
                    My Drawings
                  </Link>
                )}

                {/* Smart breadcrumb collapse - Google Drive style */}
                {(() => {
                  const parents = breadcrumbs.slice(0, -1);

                  // Show all if shallow (≤ 4 total segments including Home and Current)
                  if (breadcrumbs.length <= 4) {
                    return parents.map((folder, index) => (
                      <Link
                        key={folder.id}
                        component="button"
                        variant="body2"
                        underline="hover"
                        onClick={() => handleBreadcrumbClick(index)}
                        sx={{
                          cursor: "pointer",
                          color: "#4A5565",
                          fontFamily: "Roboto, sans-serif",
                          fontSize: "16px",
                          padding: "8px",
                          borderRadius: "4px",
                          transition: "all 0.2s",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: "200px",
                          "&:hover": {
                            color: "#2932E5",
                            bgcolor: "rgba(41, 50, 229, 0.08)",
                          },
                        }}
                        title={(folder.name || "").trim()}
                      >
                        {(folder.name || "").trim()}
                      </Link>
                    ));
                  }

                  // For deeper paths (> 4), show first folder + "..." + last 2 folders
                  const result = [];

                  // First folder after home
                  if (parents.length > 0) {
                    result.push(
                      <Link
                        key={parents[0].id}
                        component="button"
                        variant="body2"
                        underline="hover"
                        onClick={() => handleBreadcrumbClick(0)}
                        sx={{
                          cursor: "pointer",
                          color: "#4A5565",
                          fontFamily: "Roboto, sans-serif",
                          fontSize: "16px",
                          padding: "8px",
                          borderRadius: "4px",
                          transition: "all 0.2s",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: "200px",
                          "&:hover": {
                            color: "#2932E5",
                            bgcolor: "rgba(41, 50, 229, 0.08)",
                          },
                        }}
                        title={(parents[0].name || "").trim()}
                      >
                        {(parents[0].name || "").trim()}
                      </Link>
                    );
                  }

                  // Collapse indicator with menu
                  if (parents.length > 3) {
                    result.push(
                      <Chip
                        key="collapse-indicator"
                        label="..."
                        size="small"
                        onClick={(e) => {
                          // Could add a menu here showing all intermediate folders
                          e.stopPropagation();
                        }}
                        sx={{
                          height: "24px",
                          cursor: "pointer",
                          bgcolor: "transparent",
                          color: "#4A5565",
                          fontFamily: "Roboto, sans-serif",
                          fontSize: "16px",
                          fontWeight: 500,
                          "& .MuiChip-label": {
                            px: 1,
                          },
                          "&:hover": {
                            bgcolor: "rgba(41, 50, 229, 0.08)",
                          },
                        }}
                      />
                    );
                  }

                  // Last 2 folders before current
                  const lastTwoStart = Math.max(1, parents.length - 2);
                  for (let i = lastTwoStart; i < parents.length; i++) {
                    result.push(
                      <Link
                        key={parents[i].id}
                        component="button"
                        variant="body2"
                        underline="hover"
                        onClick={() => handleBreadcrumbClick(i)}
                        sx={{
                          cursor: "pointer",
                          color: "#4A5565",
                          fontFamily: "Roboto, sans-serif",
                          fontSize: "16px",
                          padding: "8px",
                          borderRadius: "4px",
                          transition: "all 0.2s",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: "200px",
                          "&:hover": {
                            color: "#2932E5",
                            bgcolor: "rgba(41, 50, 229, 0.08)",
                          },
                        }}
                        title={(parents[i].name || "").trim()}
                      >
                        {(parents[i].name || "").trim()}
                      </Link>
                    );
                  }
                  return result;
                })()}

                {/* Current folder */}
                {breadcrumbs.length > 0 && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#212121",
                      fontWeight: 500,
                      fontFamily: "Roboto, sans-serif",
                      fontSize: "16px", // Increased from 14px (+2pt)
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "300px",
                    }}
                    title={(
                      breadcrumbs[breadcrumbs.length - 1].name || ""
                    ).trim()}
                  >
                    {(breadcrumbs[breadcrumbs.length - 1].name || "").trim()}
                  </Typography>
                )}
              </Breadcrumbs>
            </Tooltip>

            <TabSettingsComponent
              title={"My Drawings Settings"}
              tooltipText={"My Drawings Settings"}
              // onOpenChange={handleSettingsDialogToggle}
              tabKey="my-drawings"
            />
          </Box>

          {/* ═══════════════════════════════════════════════════════════ */}
          {/* ROW 2: TOOLBAR ACTIONS (36px Height) - Seamless integration */}
          {/* ═══════════════════════════════════════════════════════════ */}
          <Box
            sx={{
              px: 2,
              py: 0.75,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              minHeight: "36px",
            }}
          >
            {/* LEFT SIDE: Search + View Toggle */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flex: 1,
              }}
            >
              {/* Search (FIRST - Primary action) */}
              <TextField
                size="small"
                placeholder="Search files..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: 200,
                  "& .MuiOutlinedInput-root": {
                    height: 36,
                    fontFamily: "Roboto, sans-serif",
                    fontSize: "14px",
                    bgcolor: "white",
                    padding: 0,
                    borderRadius: "10px !important",
                  },
                  "& .MuiInputAdornment-root": {
                    marginLeft: "8px",
                  },
                }}
              />
              {displaySettings?.enableFavorites !== false && (
                <FavoritesButton
                  files={files}
                  currentFolder={currentFolder}
                  showFavoritesOnly={showFavoritesOnly}
                  setShowFavoritesOnly={setShowFavoritesOnly}
                  handleFavoritesClick={handleFavoritesClick}
                />
              )}
              {/* View Toggle (SECOND - Immediately after search) */}
              <ViewToggle
                view={viewMode}
                // defaultView={displaySettings?.defaultView}
                onChange={handleViewModeChange}
                ariaLabel="Toggle file view mode"
              />
            </Box>

            {/* RIGHT SIDE: Actions */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              {/* Help Button */}
              <Tooltip
                title={
                  <Box
                    sx={{
                      p: 0.5,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        display: "block",
                        mb: 0.5,
                      }}
                    >
                      Tree Navigation:
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "11px",
                        display: "block",
                      }}
                    >
                      • Click folder to expand/collapse
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "11px",
                        display: "block",
                      }}
                    >
                      • Double-click folder to navigate
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "11px",
                        display: "block",
                      }}
                    >
                      • Ctrl+Click for multi-select
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "11px",
                        display: "block",
                      }}
                    >
                      • Shift+Click for range select
                    </Typography>
                    <Divider
                      sx={{
                        my: 0.5,
                        bgcolor: "rgba(255,255,255,0.2)",
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        display: "block",
                        mb: 0.5,
                        mt: 0.5,
                      }}
                    >
                      Keyboard Shortcuts:
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "11px",
                        display: "block",
                      }}
                    >
                      • Ctrl+Shift+E: Expand all
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "11px",
                        display: "block",
                      }}
                    >
                      • Ctrl+Shift+C: Collapse all
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "11px",
                        display: "block",
                      }}
                    >
                      • Ctrl+Shift+B: Go to parent
                    </Typography>
                  </Box>
                }
                arrow
                placement="bottom"
              >
                <IconButton
                  size="small"
                  sx={{
                    width: 36,
                    height: 36,
                    color: "#212121",
                    "&:hover": {
                      bgcolor: "rgba(41, 50, 229, 0.04)",
                    },
                  }}
                >
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              {/* Expand All Button */}
              {displayedFiles.some((f) => f.type === "folder") && (
                <Tooltip title="Expand All Folders">
                  <IconButton
                    size="small"
                    onClick={() => {
                      const allFolderIds = new Set();
                      const getAllFolderIds = (nodes) => {
                        nodes.forEach((node) => {
                          if (node.type === "folder") {
                            allFolderIds.add(node.id);
                            if (node.children) {
                              getAllFolderIds(node.children);
                            }
                          }
                        });
                      };
                      const currentFiles = currentFolder
                        ? currentFolder.children || []
                        : files;
                      getAllFolderIds(currentFiles);
                      updateExpandedFolders(allFolderIds);
                      toast.success("All folders expanded");
                    }}
                    sx={{
                      width: 36,
                      height: 36,
                      color: "#212121",
                      "&:hover": {
                        bgcolor: "rgba(41, 50, 229, 0.04)",
                      },
                    }}
                  >
                    <FolderOpenIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              {/* Collapse All Button */}
              {displayedFiles.some((f) => f.type === "folder") && (
                <Tooltip title="Collapse All Folders">
                  <IconButton
                    size="small"
                    onClick={() => {
                      updateExpandedFolders(new Set());
                      toast.success("All folders collapsed");
                    }}
                    sx={{
                      width: 36,
                      height: 36,
                      color: "#212121",
                      "&:hover": {
                        bgcolor: "rgba(41, 50, 229, 0.04)",
                      },
                    }}
                  >
                    <FolderIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <CreteNewButtonComponet
                setShowAddFolderDialog={setShowAddFolderDialog}
                setShowAddDrawingDialog={setShowAddDrawingDialog}
                isCreatingFolder={isCreatingFolder}
                isLoadingDirectories={isLoadingDirectories}
              />
            </Box>
          </Box>
        </Box>
        <BulkSelectionToolbar
          selectedIds={selectedIds}
          enableFavorites={displaySettings?.enableFavorites !== false}
        />

        {/* File List View */}
        {viewMode === "list" && (
          <TableContainer
            ref={tableContainerRef}
            onClick={(e) => {}}
            sx={{
              flex: 1,
              overflow: "auto",
              overflowX: "auto",
              // Enable horizontal scroll when needed
              overflowY: "auto", // Enable vertical scroll
            }}
          >
            <Table
              stickyHeader
              size="small"
              sx={{
                "& .MuiTableCell-root": {
                  py: 0.5,
                  px: 2,
                  whiteSpace: "nowrap",
                  // Prevent text wrapping in all cells
                  height: "36px",
                },
                minWidth: 900, // Ensure table has minimum width for horizontal scroll
                height: displayedFiles.length === 0 ? "100%" : "auto",
              }}
            >
              <TableHead
                sx={{
                  bgcolor: "#FAFAFA",
                  borderBottom: "1px solid #E5E7EB",
                  "& .MuiTableRow-root": {
                    height: "36px",
                  },
                  "& .MuiTableCell-root": {
                    height: "36px",
                    py: 0.75,
                  },
                }}
              >
                <TableRow>
                  <TableCell
                    onClick={
                      sortableColumn === "name"
                        ? () => handleSort("name")
                        : undefined
                    }
                    sx={{
                      cursor: sortableColumn === "name" ? "pointer" : "default",
                      userSelect: "none",
                      // No width specified - takes remaining space after fixed columns
                      minWidth: 100, // Absolute minimum for file names
                      maxWidth: "100%", // Don't exceed container
                      textAlign: "left",
                      overflow: "hidden", // Prevent overflow
                      textOverflow: "ellipsis", // Show ... for long names
                      "&:hover": {
                        bgcolor:
                          sortableColumn === "name"
                            ? "rgba(41, 50, 229, 0.06)"
                            : "transparent",
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {/* Icon placeholder for visual alignment with body rows */}
                      <FolderIcon
                        fontSize="small"
                        sx={{
                          color: "#2932E5",
                          opacity: 0.4,
                          width: 20,
                          height: 20,
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "Roboto, sans-serif",
                          fontWeight: 500,
                          fontSize: "14px",
                          color: "#2932E5",
                          letterSpacing: "0.1px",
                        }}
                      >
                        Name
                      </Typography>
                      {sortableColumn === "name" &&
                        sortField === "name" &&
                        (sortDirection === "asc" ? (
                          <ArrowUpwardIcon
                            fontSize="small"
                            sx={{ color: "#2932E5" }}
                          />
                        ) : (
                          <ArrowDownwardIcon
                            fontSize="small"
                            sx={{ color: "#2932E5" }}
                          />
                        ))}
                    </Box>
                  </TableCell>
                  <TableCell
                    onClick={
                      sortableColumn === "type"
                        ? () => handleSort("type")
                        : undefined
                    }
                    sx={{
                      cursor: sortableColumn === "type" ? "pointer" : "default",
                      userSelect: "none",
                      width: 80, // Further reduced to eliminate scroll
                      minWidth: 80,
                      textAlign: "left",
                      "&:hover": {
                        bgcolor:
                          sortableColumn === "type"
                            ? "rgba(41, 50, 229, 0.06)"
                            : "transparent",
                      },
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "Roboto, sans-serif",
                          fontWeight: 500,
                          fontSize: "14px",
                          color: "#2932E5",
                          letterSpacing: "0.1px",
                        }}
                      >
                        Type
                      </Typography>
                      {sortableColumn === "type" &&
                        sortField === "type" &&
                        (sortDirection === "asc" ? (
                          <ArrowUpwardIcon
                            fontSize="small"
                            sx={{ color: "#2932E5" }}
                          />
                        ) : (
                          <ArrowDownwardIcon
                            fontSize="small"
                            sx={{ color: "#2932E5" }}
                          />
                        ))}
                    </Box>
                  </TableCell>
                  <TableCell
                    onClick={
                      sortableColumn === "owner"
                        ? () => handleSort("owner")
                        : undefined
                    }
                    sx={{
                      cursor:
                        sortableColumn === "owner" ? "pointer" : "default",
                      userSelect: "none",
                      width: 100, // Further reduced to eliminate scroll
                      minWidth: 100,
                      textAlign: "left",
                      "&:hover": {
                        bgcolor:
                          sortableColumn === "owner"
                            ? "rgba(41, 50, 229, 0.06)"
                            : "transparent",
                      },
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "Roboto, sans-serif",
                          fontWeight: 500,
                          fontSize: "14px",
                          color: "#2932E5",
                          letterSpacing: "0.1px",
                        }}
                      >
                        Owner
                      </Typography>
                      {sortableColumn === "owner" &&
                        sortField === "owner" &&
                        (sortDirection === "asc" ? (
                          <ArrowUpwardIcon
                            fontSize="small"
                            sx={{ color: "#2932E5" }}
                          />
                        ) : (
                          <ArrowDownwardIcon
                            fontSize="small"
                            sx={{ color: "#2932E5" }}
                          />
                        ))}
                    </Box>
                  </TableCell>
                  {displaySettings.showModifiedDate && (
                    <TableCell
                      onClick={
                        sortableColumn === "dateModified"
                          ? () => handleSort("dateModified")
                          : undefined
                      }
                      sx={{
                        cursor:
                          sortableColumn === "dateModified"
                            ? "pointer"
                            : "default",
                        userSelect: "none",
                        width: 95, // Further reduced (date format DDMMMYY fits in ~95px)
                        minWidth: 95,
                        textAlign: "left",
                        "&:hover": {
                          bgcolor:
                            sortableColumn === "dateModified"
                              ? "rgba(41, 50, 229, 0.06)"
                              : "transparent",
                        },
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "Roboto, sans-serif",
                            fontWeight: 500,
                            fontSize: "14px",
                            color: "#2932E5",
                            letterSpacing: "0.1px",
                          }}
                        >
                          Modified
                        </Typography>
                        {sortableColumn === "dateModified" &&
                          sortField === "dateModified" &&
                          (sortDirection === "asc" ? (
                            <ArrowUpwardIcon
                              fontSize="small"
                              sx={{ color: "#2932E5" }}
                            />
                          ) : (
                            <ArrowDownwardIcon
                              fontSize="small"
                              sx={{ color: "#2932E5" }}
                            />
                          ))}
                      </Box>
                    </TableCell>
                  )}
                  <TableCell
                    sx={{ width: 65, minWidth: 65, textAlign: "left" }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                        fontWeight: 500,
                        fontSize: "14px",
                        color: "#2932E5",
                        letterSpacing: "0.1px",
                      }}
                    >
                      Items
                    </Typography>
                  </TableCell>
                  {displaySettings.showFileSize && (
                    <TableCell
                      onClick={
                        sortableColumn === "size"
                          ? () => handleSort("size")
                          : undefined
                      }
                      sx={{
                        cursor:
                          sortableColumn === "size" ? "pointer" : "default",
                        userSelect: "none",
                        width: 85, // Further reduced to eliminate scroll
                        minWidth: 85,
                        textAlign: "left",
                        "&:hover": {
                          bgcolor:
                            sortableColumn === "size"
                              ? "rgba(41, 50, 229, 0.06)"
                              : "transparent",
                        },
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "Roboto, sans-serif",
                            fontWeight: 500,
                            fontSize: "14px",
                            color: "#2932E5",
                            letterSpacing: "0.1px",
                          }}
                        >
                          Size
                        </Typography>
                        {sortableColumn === "size" &&
                          sortField === "size" &&
                          (sortDirection === "asc" ? (
                            <ArrowUpwardIcon
                              fontSize="small"
                              sx={{ color: "#2932E5" }}
                            />
                          ) : (
                            <ArrowDownwardIcon
                              fontSize="small"
                              sx={{ color: "#2932E5" }}
                            />
                          ))}
                      </Box>
                    </TableCell>
                  )}
                  <TableCell sx={{ width: 40, minWidth: 40 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedFiles.map((file, index) => {
                  const isSelected = selectedIds.has(file.id);
                  const isExpanded = expandedFolders.has(file.id);
                  const isHovered = hoveredRowId === file.id;
                  const isActiveDrawing =
                    selectedDrawingForView?.id === file.id;
                  // Check if this file is a child of the hovered parent
                  const isChildOfHoveredParent =
                    hoveredRowId && file.parentId === hoveredRowId;
                  const shouldShowCheckbox =
                    isHovered || isSelected || isChildOfHoveredParent;
                  return (
                    <TableRow
                      key={file.id}
                      selected={isSelected}
                      onClick={(e) => handleRowClick(file, index, e)}
                      onDoubleClick={() => handleRowDoubleClick(file)}
                      onContextMenu={(e) => handleContextMenu(e, file)}
                      onMouseEnter={() => setHoveredRowId(file.id)}
                      onMouseLeave={() => setHoveredRowId(null)}
                      sx={{
                        height: 36,
                        cursor: "pointer",
                        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                        // Subtle alternating depth backgrounds
                        ...(!isSelected &&
                          !isActiveDrawing &&
                          file.depth &&
                          file.depth > 0 && {
                            bgcolor:
                              file.depth % 2 === 0
                                ? "rgba(242, 242, 242, 0.3)"
                                : "rgba(255, 224, 208, 0.05)",
                          }),
                        ...(isActiveDrawing && {
                          bgcolor: "#FFE0D0 !important",
                          borderLeft: "3px solid #2932E5",
                          "&:hover": {
                            bgcolor: "#FFD0B8 !important",
                          },
                        }),
                        "&.Mui-selected": {
                          bgcolor: "rgba(41, 50, 229, 0.08)",
                          borderLeft: "3px solid",
                          borderLeftColor: "#2932E5",
                          "&:hover": {
                            bgcolor: "rgba(41, 50, 229, 0.12)",
                          },
                        },
                        "&:hover": {
                          bgcolor: "rgba(0, 0, 0, 0.04)",
                          transform: "translateX(2px)",
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          borderBottom: "1px solid #E5E7EB",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            pl: file.depth ? `${file.depth * 24}px` : 0,
                            position: "relative",
                          }}
                        >
                          {/* Visual Tree Connection Lines */}
                          {file.depth > 0 && (
                            <>
                              {/* Depth Level Indicator Dots */}
                              {Array.from({
                                length: file.depth,
                              }).map((_, idx) => (
                                <Box
                                  key={idx}
                                  sx={{
                                    position: "absolute",
                                    left: `${idx * 24 + 10}px`,
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    width: "4px",
                                    height: "4px",
                                    borderRadius: "50%",
                                    bgcolor:
                                      idx === file.depth - 1
                                        ? "#FF8555"
                                        : "#E5E7EB",
                                    transition: "all 0.2s ease-in-out",
                                  }}
                                />
                              ))}
                              {/* Vertical line from parent */}
                              <Box
                                sx={{
                                  position: "absolute",
                                  left: `${(file.depth - 1) * 24 + 12}px`,
                                  top: "-50%",
                                  bottom: "50%",
                                  width: "1px",
                                  bgcolor: "#E5E7EB",
                                }}
                              />
                              {/* Horizontal line to item */}
                              <Box
                                sx={{
                                  position: "absolute",
                                  left: `${(file.depth - 1) * 24 + 12}px`,
                                  top: "50%",
                                  width: `${file.type === "folder" ? 8 : 12}px`,
                                  height: "1px",
                                  bgcolor: "#E5E7EB",
                                }}
                              />
                            </>
                          )}

                          {/* Folder Expand/Collapse Button with Animation */}
                          {file.type === "folder" && (
                            <IconButton
                              size="small"
                              onClick={(e) => toggleFolder(file.id, e)}
                              aria-label={
                                isExpanded
                                  ? `Collapse ${file.name}`
                                  : `Expand ${file.name}`
                              }
                              aria-expanded={isExpanded}
                              sx={{
                                width: "28px",
                                minWidth: "28px",
                                maxWidth: "28px",
                                height: "28px",
                                p: 0.25,
                                mr: 0.5,
                                transition: "transform 0.2s ease-in-out",
                                transform: isExpanded
                                  ? "rotate(0deg)"
                                  : "rotate(0deg)",
                              }}
                            >
                              {isExpanded ? (
                                <ExpandMoreIcon
                                  fontSize="small"
                                  sx={{
                                    transition: "transform 0.2s ease-in-out",
                                    color: "#2932E5",
                                  }}
                                />
                              ) : (
                                <ChevronRightIcon
                                  fontSize="small"
                                  sx={{
                                    transition: "transform 0.2s ease-in-out",
                                    color: "#4A5565",
                                  }}
                                />
                              )}
                            </IconButton>
                          )}

                          {/* Spacer for files to align with folders */}
                          {file.type === "file" && (
                            <Box
                              sx={{
                                width: "28px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <SubfolderIcon
                                sx={{
                                  fontSize: "14px",
                                  color: "#E5E7EB",
                                }}
                              />
                            </Box>
                          )}

                          {/* Enhanced Folder Icon with Open/Closed States */}
                          <Box
                            sx={{
                              color: "action.active",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            {file.type === "folder" ? (
                              isExpanded ? (
                                <FolderOpenIcon
                                  sx={{
                                    color: "#2932E5",
                                    fontSize: "20px",
                                  }}
                                />
                              ) : (
                                <FolderIcon
                                  sx={{
                                    color: "#4A5565",
                                    fontSize: "20px",
                                  }}
                                />
                              )
                            ) : (
                              getFileIcon(file, isExpanded)
                            )}
                          </Box>
                          <Tooltip
                            title={
                              file.depth && file.depth > 0 ? (
                                <Box>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontWeight: 600,
                                      display: "block",
                                      mb: 0.5,
                                    }}
                                  >
                                    {file.name}
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.5,
                                    }}
                                  >
                                    <TreeIcon
                                      sx={{
                                        fontSize: "12px",
                                      }}
                                    />
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontSize: "11px",
                                      }}
                                    >
                                      Depth Level: {file.depth + 1}
                                    </Typography>
                                  </Box>
                                </Box>
                              ) : (
                                file.name
                              )
                            }
                            arrow
                            placement="top"
                          >
                            <Typography
                              variant="body2"
                              component="span"
                              sx={{
                                fontSize: "14px",
                                cursor:
                                  file.type === "file" ? "pointer" : "inherit",
                                userSelect: "none",
                                "&:hover":
                                  file.type === "file"
                                    ? {
                                        color: "#2932E5",
                                        textDecoration: "underline",
                                      }
                                    : {},
                              }}
                              onClick={(e) => {
                                if (file.type === "file") {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  handleFileNameClick(file, e);
                                }
                              }}
                            >
                              {getNamewithTrimmed(
                                file.name,
                                configrationData.list_file_name_trim_length
                              )}
                            </Typography>
                          </Tooltip>
                          <IconButton
                            size="small"
                            onClick={(e) => handleStarToggle(file, e)}
                            sx={{
                              p: 0.25,
                              ml: 0.5,
                              opacity: file.starred || isHovered ? 1 : 0,
                              transition: "opacity 0.2s",
                            }}
                            disabled={
                              disableItem("star") ||
                              displaySettings?.enableFavorites === false
                            }
                          >
                            {file.starred ? (
                              <StarIcon
                                fontSize="small"
                                sx={{
                                  color: "#F4B400",
                                }}
                              />
                            ) : (
                              <StarBorderIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          borderBottom: "1px solid #E5E7EB",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "13px",
                            color: "text.secondary",
                          }}
                        >
                          {file.type}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={{
                          borderBottom: "1px solid #E5E7EB",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "13px",
                            color: "text.secondary",
                          }}
                        >
                          {file.owner || "—"}
                        </Typography>
                      </TableCell>
                      {displaySettings.showModifiedDate && (
                        <TableCell
                          sx={{
                            borderBottom: "1px solid #E5E7EB",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "13px",
                              color: "text.secondary",
                            }}
                          >
                            {formatDate(file.dateModified) || "Today"}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell
                        sx={{
                          borderBottom: "1px solid #E5E7EB",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "13px",
                            color: "text.secondary",
                          }}
                        >
                          {file.type === "folder"
                            ? getFolderItemCount(file)
                            : "—"}
                        </Typography>
                      </TableCell>
                      {displaySettings.showFileSize && (
                        <TableCell
                          sx={{
                            borderBottom: "1px solid #E5E7EB",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "13px",
                              color: "text.secondary",
                            }}
                          >
                            {file.type === "folder"
                              ? formatFolderSize(file)
                              : file.size
                              ? formatFileSize(file.size)
                              : "—"}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell
                        sx={{
                          borderBottom: "1px solid #E5E7EB",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            gap: 0.5,
                          }}
                        >
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(file);
                              }}
                              sx={{
                                opacity: isHovered ? 1 : 0,
                                transition: "opacity 0.2s",
                                "&:hover": {
                                  color: "#D32F2F",
                                },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="File information">
                            <IconButton
                              size="small"
                              onClick={async (e) => {
                                e.stopPropagation();
                                setInfoFile(file);
                                setShowInfoPanel(true);
                                setInfoTabValue(0); // Reset to Details tab
                                setActivities([]); // Clear previous activities
                                // Fetch activities when info panel opens
                                if (file?.id) {
                                  await fetchActivities(file.id);
                                }
                              }}
                              sx={{
                                opacity: isHovered ? 1 : 0,
                                transition: "opacity 0.2s",
                              }}
                            >
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuClick(e, file)}
                            sx={{
                              opacity: isHovered ? 1 : 0,
                              transition: "opacity 0.2s",
                            }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {displayedFiles.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={
                        3 +
                        (displaySettings.showModifiedDate ? 1 : 0) +
                        (displaySettings.showFileSize ? 1 : 0) +
                        2
                      }
                      align="center"
                    >
                      <EmptyDirectoryComponent
                        searchQuery={searchQuery}
                        showFavoritesOnly={showFavoritesOnly}
                        handleFavoritesClick={handleFavoritesClick}
                      />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* File Grid View */}
        {viewMode === "grid" && (
          <Box
            onClick={(e) => {}}
            sx={{
              flex: 1,
              overflow: "auto",
              p: 1,
            }}
          >
            <Grid container spacing={1} sx={{ height: "100%", width: "100%" }}>
              {displayedFiles.map((file, index) => {
                const isSelected = selectedIds.has(file.id);
                const isExpanded = expandedFolders.has(file.id);
                const isHovered = hoveredRowId === file.id;
                const isActiveDrawing = selectedDrawingForView?.id === file.id;
                // Check if this file is a child of the hovered parent
                const isChildOfHoveredParent =
                  hoveredRowId && file.parentId === hoveredRowId;
                const shouldShowCheckbox =
                  isHovered || isSelected || isChildOfHoveredParent;
                return (
                  <Grid
                    item
                    key={file.id}
                    // xs={12}
                    // sm={6}
                    // md={4}
                    // lg={3}
                    // xl={2}
                    sx={{
                      ml: file.depth ? `${file.depth * 16}px` : 0,
                      position: "relative",
                      width: "218px",
                      height: "317px",
                    }}
                  >
                    <Card
                      onMouseEnter={() => setHoveredRowId(file.id)}
                      onMouseLeave={() => setHoveredRowId(null)}
                      onContextMenu={(e) => handleContextMenu(e, file)}
                      sx={{
                        position: "relative",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        cursor: "pointer",
                        border: "1px solid #E5E7EB",
                        borderRadius: "14px",
                        width: "100%",
                        height: "100%",
                        backgroundColor: "rgb(255, 255, 255)",
                        color: "rgb(33, 33, 33)",
                        boxshadow: "var(--Paper-shadow)",
                        backgroundImage: "var(--Paper-overlay)",
                        overflow: "hidden",
                        borderRadius: "14px",
                        transition: "0.3s cubic-bezier(0.4, 0, 0.2, 1)",

                        animation: "auto ease 0s 1 normal none running none",
                        // Add colored left border based on depth
                        ...(file.depth &&
                          file.depth > 0 &&
                          !isSelected &&
                          !isActiveDrawing && {
                            borderLeft: `4px solid ${
                              file.depth === 1
                                ? "#2932E5"
                                : file.depth === 2
                                ? "#FF8555"
                                : file.depth === 3
                                ? "#4A5565"
                                : "#E5E7EB"
                            }`,
                          }),
                        animation:
                          file.depth && file.depth > 0
                            ? "slideIn 0.3s ease-out"
                            : "none",
                        "@keyframes slideIn": {
                          from: {
                            opacity: 0,
                            transform: "translateX(-16px)",
                          },
                          to: {
                            opacity: 1,
                            transform: "translateX(0)",
                          },
                        },
                        ...(isSelected && {
                          bgcolor: "rgba(41, 50, 229, 0.08)",
                          border: "2px solid #2932E5",
                        }),
                        ...(isActiveDrawing && {
                          bgcolor: "#FFE0D0",
                          border: "2px solid #FF8555",
                        }),
                        "&:hover": {
                          boxShadow: "0 8px 16px rgba(0,0,0,0.12)",
                          transform: "translateY(-4px) scale(1.02)",
                        },
                      }}
                    >
                      {/* Depth Level Badge */}
                      {file.depth > 0 && (
                        <Chip
                          icon={
                            <SubfolderIcon
                              sx={{
                                fontSize: "12px !important",
                              }}
                            />
                          }
                          label={`L${file.depth + 1}`}
                          size="small"
                          sx={{
                            position: "absolute",
                            top: 8,
                            left: 8,
                            bgcolor: "#FFE0D0",
                            color: "#FF8555",
                            fontWeight: 600,
                            fontSize: "10px",
                            height: "20px",
                            zIndex: 2,
                            "& .MuiChip-icon": {
                              color: "#FF8555",
                            },
                          }}
                        />
                      )}

                      {/* Expand/Collapse for folders */}
                      {file.type === "folder" && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFolder(file.id, e);
                          }}
                          aria-label={
                            isExpanded
                              ? `Collapse ${file.name}`
                              : `Expand ${file.name}`
                          }
                          aria-expanded={isExpanded}
                          sx={{
                            position: "absolute",
                            top: file.depth && file.depth > 0 ? 34 : 8,
                            left: 8,
                            bgcolor: "white",
                            zIndex: 2,
                            transition: "all 0.2s ease-in-out",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                            "&:hover": {
                              bgcolor: "#F2F2F2",
                              transform: "scale(1.15)",
                              boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                            },
                          }}
                        >
                          {isExpanded ? (
                            <ExpandMoreIcon
                              fontSize="small"
                              sx={{
                                color: "#2932E5",
                                transition: "transform 0.2s ease-in-out",
                              }}
                            />
                          ) : (
                            <ChevronRightIcon
                              fontSize="small"
                              sx={{
                                color: "#4A5565",
                                transition: "transform 0.2s ease-in-out",
                              }}
                            />
                          )}
                        </IconButton>
                      )}

                      <Box
                        onClick={(e) => handleRowClick(file, index, e)}
                        onDoubleClick={() => handleRowDoubleClick(file)}
                        sx={{
                          p: "24px",
                          // pt: file.depth && file.depth > 0 ? 4 : 1,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        {/* File/Folder Thumbnail with Enhanced Icons */}
                        {file.type === "folder" ? (
                          <Box
                            sx={{
                              width: 200,
                              height: 200,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              bgcolor: "#F2F2F2",
                              borderRadius: 1,
                              transition: "all 0.2s ease-in-out",
                            }}
                          >
                            {isExpanded ? (
                              <FolderOpenIcon
                                sx={{
                                  fontSize: 120,
                                  color: "#2932E5",
                                  transition: "all 0.2s ease-in-out",
                                }}
                              />
                            ) : (
                              <FolderIcon
                                sx={{
                                  fontSize: 120,
                                  color: "#4A5565",
                                  transition: "all 0.2s ease-in-out",
                                }}
                              />
                            )}
                          </Box>
                        ) : displaySettings.showThumbnails ? (
                          <FileThumbnail
                            fileType={file.fileType}
                            fileUrl={file.fileUrl}
                            fileName={file.name}
                            type={file.type}
                            size={200}
                            thumbnailUrl={file.thumbnailUrl}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: 200,
                              height: 200,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              bgcolor: "#F2F2F2",
                              borderRadius: 1,
                              transition: "all 0.2s ease-in-out",
                            }}
                          >
                            {getFileIcon(file, isExpanded)}
                          </Box>
                        )}

                        {/* File Name */}
                        <Tooltip title={file.name} placement="top">
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "13px",
                              fontWeight: 500,
                              textAlign: "center",
                              width: "100%",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              color: "#212121",
                              px: 0.2,
                              mt: 0.2,
                            }}
                            onClick={(e) => {
                              if (file.type === "file") {
                                e.stopPropagation();
                                e.preventDefault();
                                handleFileNameClick(file, e);
                              }
                            }}
                          >
                            {file.name}
                          </Typography>
                        </Tooltip>

                        {/* Metadata */}
                        <Box
                          sx={{
                            display: "flex",
                            gap: 0.2,
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {file.type === "folder" && file.children && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#4A5565",
                                fontSize: "11px",
                              }}
                            >
                              {file.children.length} items
                            </Typography>
                          )}
                          {file.type === "file" &&
                            file.size &&
                            displaySettings.showFileSize && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "#4A5565",
                                  fontSize: "11px",
                                }}
                              >
                                {formatFileSize(file.size)}
                              </Typography>
                            )}
                        </Box>

                        {/* Owner Badge */}
                        {file.owner && (
                          <Box
                            sx={{
                              display: "flex",
                              gap: 0.25,
                              alignItems: "center",
                            }}
                          >
                            <Avatar
                              sx={{
                                width: 14,
                                height: 14,
                                fontSize: "9px",
                                bgcolor: "#2932E5",
                              }}
                            >
                              {file.owner.charAt(0)}
                            </Avatar>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#4A5565",
                                fontSize: "10px",
                              }}
                            >
                              {file.owner}
                            </Typography>
                          </Box>
                        )}

                        {/* Star Icon */}
                        {file.starred &&
                          !disableItem("star") &&
                          displaySettings?.enableFavorites !== false && (
                            <StarIcon
                              disabled={disableItem("star")}
                              fontSize="small"
                              sx={{
                                position: "absolute",
                                bottom: 8,
                                right: 8,
                                color: "#FFB300",
                              }}
                            />
                          )}
                      </Box>

                      {/* 3-dot menu on hover */}
                      {isHovered && (
                        <IconButton
                          size="small"
                          aria-label={`More options for ${file.name}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleMenuClick(e, file);
                          }}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                            // Only stop propagation, don't preventDefault to avoid interfering with click positioning
                          }}
                          onMouseUp={(e) => {
                            e.stopPropagation();
                            // Only stop propagation, don't preventDefault to avoid interfering with click positioning
                          }}
                          sx={{
                            position: "absolute",
                            bottom: 8,
                            left: 8,
                            bgcolor: "white",
                            zIndex: 10,
                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                            "&:hover": {
                              bgcolor: "#F2F2F2",
                            },
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Card>
                  </Grid>
                );
              })}
              {displayedFiles.length === 0 && (
                <Grid item xs={12}>
                  <EmptyDirectoryComponent
                    searchQuery={searchQuery}
                    showFavoritesOnly={showFavoritesOnly}
                    handleFavoritesClick={handleFavoritesClick}
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        )}

        {/* Enhanced Status Bar with Tree Statistics */}
        <Box
          sx={{
            px: 2,
            py: 1,
            bgcolor: "#F2F2F2",
            borderTop: "1px solid #E5E7EB",
            display: "flex",
            alignItems: "center",
            gap: 3,
            flexWrap: "wrap",
          }}
        >
          {/* Total Items (Folders + Files) */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "#4A5565",
                fontWeight: 500,
              }}
            >
              Total Items:
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "#212121",
                fontWeight: 600,
              }}
            >
              {displayedFiles.filter((f) => f.type === "folder").length +
                displayedFiles.filter((f) => f.type === "file").length}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <FolderIcon
              sx={{
                fontSize: "14px",
                color: "#4A5565",
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: "#4A5565",
                fontWeight: 500,
              }}
            >
              Folders:
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "#212121",
                fontWeight: 600,
              }}
            >
              {displayedFiles.filter((f) => f.type === "folder").length}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <FileIcon
              sx={{
                fontSize: "14px",
                color: "#4A5565",
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: "#4A5565",
                fontWeight: 500,
              }}
            >
              Files:
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "#212121",
                fontWeight: 600,
              }}
            >
              {displayedFiles.filter((f) => f.type === "file").length}
            </Typography>
          </Box>

          {/* Max Depth */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <TreeIcon
              sx={{
                fontSize: "14px",
                color: "rgb(255, 133, 85)",
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: "#4A5565",
                fontWeight: 500,
              }}
            >
              Max Depth:
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "rgb(255, 133, 85)",
                fontWeight: 600,
              }}
            >
              Level {currentMaxDepth + 1}
            </Typography>
          </Box>

          {/* Number of Selected Items */}
          {(() => {
            // Calculate selected count based on currently displayed files only
            // When inside a folder, exclude the folder itself from count
            const displayedFileIds = new Set(displayedFiles.map((f) => f.id));
            const selectedInCurrentView = Array.from(selectedIds).filter(
              (id) => {
                // Only count items that are in the current displayed view
                if (!displayedFileIds.has(id)) return false;
                // If we're inside a folder, exclude the folder itself from count
                if (currentFolder && id === currentFolder.id) return false;
                return true;
              }
            );
            const selectedCount = selectedInCurrentView.length;
            return selectedCount > 0 ? (
              <>
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{
                    bgcolor: "#E5E7EB",
                  }}
                />
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#2932E5",
                      fontWeight: 500,
                    }}
                  >
                    Selected:
                  </Typography>
                  <Chip
                    label={selectedCount}
                    size="small"
                    sx={{
                      bgcolor: "#2932E5",
                      color: "white",
                      fontWeight: 600,
                      fontSize: "11px",
                      height: "20px",
                    }}
                  />
                </Box>
              </>
            ) : null;
          })()}

          {/* {selectedIds.size > 0 && (
            <>
              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  bgcolor: "#E5E7EB",
                }}
              />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: "#2932E5",
                    fontWeight: 500,
                  }}
                >
                  Selected:
                </Typography>
                <Chip
                   label={selectedCount}
                  size="small"
                  sx={{
                    bgcolor: "#2932E5",
                    color: "white",
                    fontWeight: 600,
                    fontSize: "11px",
                    height: "20px",
                  }}
                />
              </Box>
            </>
          )} */}

          {/* Currently Active Drawing */}
          {selectedDrawingForView && (
            <>
              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  bgcolor: "#E5E7EB",
                }}
              />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <PdfIcon
                  sx={{
                    fontSize: "14px",
                    color: "#FF8555",
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: "#4A5565",
                    fontWeight: 500,
                  }}
                >
                  Active Drawing:
                </Typography>
                <Tooltip title={selectedDrawingForView.name}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#FF8555",
                      fontWeight: 600,
                      maxWidth: "200px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {selectedDrawingForView.name}
                  </Typography>
                </Tooltip>
              </Box>
            </>
          )}
        </Box>
      </Box>

      {/* Three-Dot Menu */}
      <Menu
        anchorEl={anchorEl?.position ? undefined : anchorEl?.element}
        anchorReference={anchorEl?.position ? "anchorPosition" : "anchorEl"}
        anchorPosition={
          anchorEl?.position
            ? {
                top: anchorEl.position.top,
                left: anchorEl.position.left,
              }
            : undefined
        }
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
        onMouseDown={(e) => {
          e.stopPropagation();
        }}
        disableAutoFocusItem
        disableScrollLock
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        MenuListProps={{
          onClick: (e) => {
            e.stopPropagation();
          },
          onMouseDown: (e) => {
            e.stopPropagation();
          },
        }}
        slotProps={{
          paper: {
            onClick: (e) => {
              e.stopPropagation();
            },
            onMouseDown: (e) => {
              e.stopPropagation();
            },
          },
        }}
        sx={{
          "& .MuiPaper-root": {
            bgcolor: "white",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            zIndex: 1300,
          },
        }}
      >
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            handleStarToggle(anchorEl.file, e);
            handleMenuClose();
          }}
          disabled={
            disableItem("star") || displaySettings?.enableFavorites === false
          }
        >
          {anchorEl?.file.starred ? (
            <StarIcon
              fontSize="small"
              sx={{
                mr: 1.5,
              }}
            />
          ) : (
            <StarBorderIcon
              fontSize="small"
              sx={{
                mr: 1.5,
              }}
            />
          )}
          {anchorEl?.file.starred ? "Unstar" : "Star"}
        </MenuItem>
        {anchorEl?.file.type === "file" && (
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              handleDownload(anchorEl.file);
            }}
          >
            <DownloadIcon
              fontSize="small"
              sx={{
                mr: 1.5,
              }}
            />
            Download
          </MenuItem>
        )}
        <Divider />
        {anchorEl?.file.type === "folder" && (
          <>
            <MenuItem
              onClick={(e) => {
                e.stopPropagation();
                handleAddSubfolder(anchorEl.file);
              }}
            >
              <CreateNewFolderIcon
                fontSize="small"
                sx={{
                  mr: 1.5,
                }}
              />
              Add Subfolder
            </MenuItem>
            <MenuItem
              onClick={(e) => {
                e.stopPropagation();
                handleAddDrawing(anchorEl.file);
              }}
            >
              <CloudUploadIcon
                fontSize="small"
                sx={{
                  mr: 1.5,
                }}
              />
              Upload Files
            </MenuItem>
            <Divider />
          </>
        )}
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            handleRename(anchorEl.file);
          }}
        >
          <EditIcon
            fontSize="small"
            sx={{
              mr: 1.5,
            }}
          />
          Rename
        </MenuItem>
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            handleMenuClose();
          }}
          disabled={disableItem("make a copy")}
        >
          <FileCopyIcon
            fontSize="small"
            sx={{
              mr: 1.5,
            }}
          />
          Make a copy
        </MenuItem>
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            handleMenuClose();
          }}
          disabled={disableItem("move to")}
        >
          <DriveFileMoveIcon
            fontSize="small"
            sx={{
              mr: 1.5,
            }}
          />
          Move to
        </MenuItem>
        <MenuItem
          onClick={async (e) => {
            e.stopPropagation();
            setInfoFile(anchorEl.file);
            setShowInfoPanel(true);
            setInfoTabValue(0); // Reset to Details tab
            setActivities([]); // Clear previous activities
            handleMenuClose();
            // Fetch activities when info panel opens
            if (anchorEl.file?.id) {
              await fetchActivities(anchorEl.file.id);
            }
          }}
          disabled={disableItem("file information")}
        >
          <InfoIcon
            fontSize="small"
            sx={{
              mr: 1.5,
            }}
          />
          File information
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            handleDelete(anchorEl.file);
          }}
          sx={{
            color: "error.main",
          }}
        >
          <DeleteIcon
            fontSize="small"
            sx={{
              mr: 1.5,
            }}
          />
          Delete
        </MenuItem>
      </Menu>

      {/* Context Menu */}
      <Menu
        open={contextMenu !== null && !anchorEl}
        onClose={handleContextMenuClose}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
        onMouseDown={(e) => {
          e.stopPropagation();
        }}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? {
                top: contextMenu.mouseY,
                left: contextMenu.mouseX,
              }
            : undefined
        }
        MenuListProps={{
          onClick: (e) => {
            e.stopPropagation();
          },
          onMouseDown: (e) => {
            e.stopPropagation();
          },
        }}
      >
        {contextMenu?.file.type === "file" && (
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              handleFilePreview(contextMenu.file);
            }}
          >
            <OpenInNewIcon
              fontSize="small"
              sx={{
                mr: 1.5,
              }}
            />
            File Preview
          </MenuItem>
        )}
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            handleStarToggle(contextMenu.file, e);
            handleContextMenuClose();
          }}
          disabled={
            disableItem("star") || displaySettings?.enableFavorites === false
          }
        >
          {contextMenu?.file.starred ? (
            <StarIcon
              fontSize="small"
              sx={{
                mr: 1.5,
              }}
            />
          ) : (
            <StarBorderIcon
              fontSize="small"
              sx={{
                mr: 1.5,
              }}
            />
          )}
          {contextMenu?.file.starred ? "Unstar" : "Star"}
        </MenuItem>
        {contextMenu?.file.type === "file" && (
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              handleDownload(contextMenu.file);
            }}
          >
            <DownloadIcon
              fontSize="small"
              sx={{
                mr: 1.5,
              }}
            />
            Download
          </MenuItem>
        )}
        <Divider />
        {contextMenu?.file.type === "folder" && (
          <>
            <MenuItem
              onClick={(e) => {
                e.stopPropagation();
                handleAddSubfolder(contextMenu.file);
              }}
            >
              <CreateNewFolderIcon
                fontSize="small"
                sx={{
                  mr: 1.5,
                }}
              />
              Add Subfolder
            </MenuItem>
            <MenuItem
              onClick={(e) => {
                e.stopPropagation();
                handleAddDrawing(contextMenu.file);
              }}
            >
              <CloudUploadIcon
                fontSize="small"
                sx={{
                  mr: 1.5,
                }}
              />
              Upload Files
            </MenuItem>
            <Divider />
          </>
        )}
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            handleRename(contextMenu.file);
          }}
        >
          <EditIcon
            fontSize="small"
            sx={{
              mr: 1.5,
            }}
          />
          Rename
        </MenuItem>
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            handleContextMenuClose();
          }}
          disabled={disableItem("make a copy")}
        >
          <FileCopyIcon
            fontSize="small"
            sx={{
              mr: 1.5,
            }}
          />
          Make a copy
        </MenuItem>
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            handleContextMenuClose();
          }}
          disabled={disableItem("move to")}
        >
          <DriveFileMoveIcon
            fontSize="small"
            sx={{
              mr: 1.5,
            }}
          />
          Move to
        </MenuItem>
        <MenuItem
          onClick={async (e) => {
            e.stopPropagation();
            setInfoFile(contextMenu.file);
            setShowInfoPanel(true);
            setInfoTabValue(0); // Reset to Details tab
            setActivities([]); // Clear previous activities
            handleContextMenuClose();
            // Fetch activities when info panel opens
            if (contextMenu.file?.id) {
              await fetchActivities(contextMenu.file.id);
            }
          }}
          disabled={disableItem("file information")}
        >
          <InfoIcon
            fontSize="small"
            sx={{
              mr: 1.5,
            }}
          />
          File information
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            handleDelete(contextMenu.file);
          }}
          sx={{
            color: "error.main",
          }}
          disabled={disableItem("delete")}
        >
          <DeleteIcon
            fontSize="small"
            sx={{
              mr: 1.5,
            }}
          />
          Delete
        </MenuItem>
      </Menu>

      {/* Add Folder Dialog */}
      <Dialog
        open={showAddFolderDialog}
        onClose={() => {
          setShowAddFolderDialog(false);
          setTargetFolder(null);
          setNewFolderName("");
          setValidationError(false);
        }}
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: "14px",
            maxWidth: "600px",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          {targetFolder ? "New Subfolder" : "New Folder"}
        </DialogTitle>
        <DialogContent sx={{ width: "400px", maxWidth: "100%" }}>
          <TextField
            autoFocus
            variant="outlined"
            margin="dense"
            placeholder="Enter folder name"
            fullWidth
            size="medium"
            value={newFolderName}
            onChange={(e) => handleOnChangeFolderName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                targetFolder ? handleCreateSubfolder() : handleAddFolder();
              }
            }}
            helperText="Folder name"
            sx={{
              borderRadius: "14px",
              "& .MuiOutlinedInput-root": {
                padding: 0,
                borderRadius: "14px",
              },
            }}
            FormHelperTextProps={{
              sx: {
                mt: 0,
                mx: 0,
              },
            }}
          />
          {validationError && (
            <DialogContentText
              sx={{ color: "error.main", display: "block", fontSize: "15px" }}
            >
              Name must be less than {configrationData.folder_length} characters
            </DialogContentText>
          )}
        </DialogContent>

        <DialogActions>
          <ButtonComponet
            onClick={() => {
              setShowAddFolderDialog(false);
              setTargetFolder(null);
              setNewFolderName("");
              setValidationError(false);
            }}
            className="text-button-variant"
          >
            Cancel
          </ButtonComponet>
          <ButtonComponet
            onClick={targetFolder ? handleCreateSubfolder : handleAddFolder}
            disabled={isCreatingFolder || validationError}
            className="contained-button-variant"
          >
            {isCreatingFolder ? "Creating..." : "Create"}
          </ButtonComponet>
        </DialogActions>
      </Dialog>

      {/* Rename Dialog */}
      <Dialog
        open={showRenameDialog}
        onClose={() => {
          setShowRenameDialog(false);
          setRenameItem(null);
          setRenameName("");
          setValidationError(false);
        }}
      >
        <DialogTitle>
          Rename {renameItem?.type === "folder" ? "Folder" : "File"}
        </DialogTitle>
        <DialogContent sx={{ width: "400px", maxWidth: "100%" }}>
          <TextField
            autoFocus
            variant="outlined"
            margin="dense"
            placeholder={`Enter new ${
              renameItem?.type === "folder" ? "folder" : "file"
            } name`}
            fullWidth
            size="medium"
            value={renameName}
            onChange={(e) =>
              handleOnChangeRenameName(e.target.value, renameItem?.type)
            }
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleRenameSubmit();
              }
            }}
            helperText={`${
              renameItem?.type === "folder" ? "Folder" : "File"
            } name`}
            sx={{
              "& .MuiOutlinedInput-root": {
                padding: 0,
              },
            }}
            FormHelperTextProps={{
              sx: {
                mt: 0,
                mx: 0,
              },
            }}
          />
          {validationError && (
            <DialogContentText
              sx={{ color: "error.main", display: "block", fontSize: "15px" }}
            >
              Name must be less than{" "}
              {renameItem?.type === "folder"
                ? configrationData.folder_length
                : configrationData.file_length}{" "}
              characters
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowRenameDialog(false);
              setRenameItem(null);
              setRenameName("");
              setValidationError(false);
            }}
            disabled={isUpdatingDirectory}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRenameSubmit}
            variant="contained"
            disabled={
              isUpdatingDirectory || !renameName.trim() || validationError
            }
          >
            {isUpdatingDirectory ? "Renaming..." : "Rename"}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Delete Confirmation Dialog */}
      {
        <Dialog
          open={deleteConfirmDialog.open}
          onClose={cancelDelete}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              color: "error.main",
            }}
          >
            <WarningIcon color="error" />
            Confirm Delete
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Are you sure you want to delete{" "}
              <strong>
                {deleteConfirmDialog.file?.type === "folder"
                  ? "folder"
                  : "file"}{" "}
                "{deleteConfirmDialog.file?.name}"?
              </strong>
            </DialogContentText>

            {deleteConfirmDialog.isChecking ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  py: 2,
                }}
              >
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  Checking for associated data...
                </Typography>
              </Box>
            ) : deleteConfirmDialog.associatedData &&
              deleteConfirmDialog.file?.type === "file" &&
              (deleteConfirmDialog.associatedData.sessionsCount > 0 ||
                deleteConfirmDialog.associatedData.pinsCount > 0 ||
                deleteConfirmDialog.associatedData.recordingsCount > 0) ? (
              <Box
                sx={{
                  bgcolor: "#FEE2E2",
                  p: 2,
                  borderRadius: 1,
                  mb: 2,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: "bold", mb: 1 }}
                >
                  Warning: This file has associated data that will also be
                  deleted:
                </Typography>
                <List dense>
                  {deleteConfirmDialog.associatedData.sessionsCount > 0 && (
                    <ListItem sx={{ py: 0.5, px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <InfoIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={`${deleteConfirmDialog.associatedData.sessionsCount} Session(s)`}
                        primaryTypographyProps={{ variant: "body2" }}
                      />
                    </ListItem>
                  )}
                  {deleteConfirmDialog.associatedData.pinsCount > 0 && (
                    <ListItem sx={{ py: 0.5, px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <InfoIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={`${deleteConfirmDialog.associatedData.pinsCount} Pin(s)`}
                        primaryTypographyProps={{ variant: "body2" }}
                      />
                    </ListItem>
                  )}
                  {deleteConfirmDialog.associatedData.recordingsCount > 0 && (
                    <ListItem sx={{ py: 0.5, px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <InfoIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={`${deleteConfirmDialog.associatedData.recordingsCount} Recording(s)`}
                        primaryTypographyProps={{ variant: "body2" }}
                      />
                    </ListItem>
                  )}
                </List>
              </Box>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={cancelDelete}
              disabled={deleteConfirmDialog.isChecking}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmDelete}
              variant="contained"
              color="error"
              disabled={deleteConfirmDialog.isChecking}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      }

      {/* Upload Files Dialog */}
      <Dialog
        open={showAddDrawingDialog}
        onClose={() => {
          setShowAddDrawingDialog(false);
          setNewDrawingName("");
          setSelectedFile(null);
          setValidationError(false);
        }}
        maxWidth="sm"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: "14px",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: "Roboto, sans-serif",
            fontSize: "20px",
            color: "#FF8555",
            fontWeight: 600,
          }}
        >
          Upload Files
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 3,
              pt: 1,
            }}
          >
            {/* File Name Input */}
            <TextField
              autoFocus
              variant="outlined"
              placeholder="Enter file name"
              fullWidth
              size="medium"
              value={newDrawingName}
              onChange={(e) => handleOnChangeDrawingName(e.target.value)}
              helperText="File Name"
              sx={{
                "& .MuiOutlinedInput-root": {
                  padding: 0,
                  borderRadius: "10px",
                },
              }}
              FormHelperTextProps={{
                sx: {
                  mt: 0,
                  mx: 0,
                },
              }}
            />
            {validationError && (
              <DialogContentText
                sx={{ color: "error.main", display: "block", fontSize: "15px" }}
              >
                Name must be less than {configrationData.file_length} characters
              </DialogContentText>
            )}
            {/* File Upload Section */}
            <Box>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                sx={{
                  height: "56px",
                  borderColor: "#E5E7EB",
                  color: "#4A5565",
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "14px",
                  textTransform: "none",
                  justifyContent: "flex-start",
                  px: 2,
                  "&:hover": {
                    borderColor: "#2932E5",
                    bgcolor: "#F2F2F2",
                    borderRadius: "10px",
                  },
                }}
              >
                {selectedFile ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <DescriptionIcon
                      sx={{
                        color: "#2932E5",
                      }}
                    />
                    <Box
                      sx={{
                        textAlign: "left",
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: "Roboto, sans-serif",
                          fontSize: "14px",
                          color: "#212121",
                        }}
                      >
                        {selectedFile.name}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: "Roboto, sans-serif",
                          fontSize: "12px",
                          color: "#4A5565",
                        }}
                      >
                        {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <CloudUploadIcon
                      sx={{
                        color: "#4A5565",
                      }}
                    />
                    <Typography
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                        fontSize: "14px",
                      }}
                    >
                      Choose file from your computer
                    </Typography>
                  </Box>
                )}
                <input
                  type="file"
                  hidden
                  accept={getAcceptedFileTypes()}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      // Strict validation: Check file type
                      if (!isSupportedFileType(file.name)) {
                        const supportedTypes =
                          getSupportedFileTypesDescription();
                        toast.error(
                          `Unsupported file type. Only the following formats are allowed: ${supportedTypes}`
                        );
                        return;
                      }

                      // Validate file size
                      const sizeValidation = validateFileSize(file.size);
                      if (!sizeValidation.valid) {
                        toast.error(sizeValidation.error);
                        return;
                      }
                      setSelectedFile(file);
                      // Auto-fill the file name if empty
                      if (!newDrawingName) {
                        const nameWithoutExtension = file.name.replace(
                          /\.[^/.]+$/,
                          ""
                        );
                        setNewDrawingName(nameWithoutExtension);
                        if (
                          !validationOnCharLength(
                            nameWithoutExtension,
                            configrationData.file_length
                          )
                        ) {
                          setValidationError(true);
                        } else {
                          setValidationError(false);
                        }
                      }
                    }
                  }}
                />
              </Button>
              <Typography
                sx={{
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "12px",
                  color: "#4A5565",
                  mt: 1,
                }}
              >
                Supported formats: {getSupportedFileTypesDescription()} (Max:
                10MB)
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 2,
          }}
        >
          <Button
            onClick={() => {
              setShowAddDrawingDialog(false);
              setNewDrawingName("");
              setSelectedFile(null);
              setValidationError(false);
            }}
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontSize: "14px",
              textTransform: "none",
              color: "#4A5565",
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateDrawing}
            variant="contained"
            disabled={
              !newDrawingName.trim() ||
              !selectedFile ||
              isCreatingFolder ||
              validationError
            }
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontSize: "14px",
              textTransform: "none",
              bgcolor: "#2932E5",
              "&:hover": {
                bgcolor: "#1e24b8",
              },
              "&:disabled": {
                bgcolor: "#E5E7EB",
                color: "#4A5565",
              },
              fontWeight: 600,
            }}
          >
            {isCreatingFolder ? "Uploading..." : "Upload Drawing"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* File Information Panel - Google Drive Style */}
      <Drawer
        anchor="right"
        open={showInfoPanel}
        onClose={() => {
          setShowInfoPanel(false);
          setActivities([]);
          setInfoTabValue(0);
        }}
        sx={{
          "& .MuiDrawer-paper": {
            width: 1000,
            bgcolor: "#FFFFFF",
          },
        }}
      >
        {infoFile && (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Header with Close Button */}
            <Box
              sx={{
                p: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                borderBottom: "1px solid #E5E7EB",
              }}
            >
              <IconButton
                size="small"
                onClick={() => {
                  setShowInfoPanel(false);
                  setActivities([]);
                  setInfoTabValue(0);
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Large Preview Thumbnail */}
            <Box
              sx={{
                p: 4,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "#F8F9FA",
                minHeight: 220,
                cursor: infoFile.type === "file" ? "pointer" : "default",
              }}
              onClick={() => {
                if (infoFile.type === "file") {
                  toast("Opening file preview...");
                }
              }}
            >
              {infoFile.type === "folder" ? (
                <FolderIcon
                  sx={{
                    fontSize: 72,
                    color: "#5F6368",
                  }}
                />
              ) : (
                <Box
                  sx={{
                    "& svg": {
                      fontSize: "72px !important",
                      color: "#5F6368",
                    },
                  }}
                >
                  {getFileIcon(infoFile, false)}
                </Box>
              )}
            </Box>

            {/* File Name & Quick Actions */}
            <Box
              sx={{
                px: 3,
                py: 2,
                borderBottom: "1px solid #E5E7EB",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontSize: "18px",
                  fontWeight: 500,
                  wordBreak: "break-word",
                  mb: 2,
                  color: "#202124",
                }}
              >
                {infoFile.name}
              </Typography>

              {/* Quick Action Buttons */}
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<OpenInNewIcon />}
                  onClick={() => toast("Opening file...")}
                  sx={{
                    fontSize: "13px",
                  }}
                  disabled={disableItem("open")}
                >
                  Open
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ShareIcon />}
                  onClick={() => toast("Share functionality coming soon")}
                  sx={{
                    fontSize: "13px",
                  }}
                  disabled={disableItem("share")}
                >
                  Share
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<LinkIcon />}
                  onClick={() => toast.success("Link copied to clipboard")}
                  sx={{
                    fontSize: "13px",
                  }}
                  disabled={disableItem("get link")}
                >
                  Get link
                </Button>
              </Box>
            </Box>

            {/* Tabs for Details and Activity */}
            <Box
              sx={{
                borderBottom: "1px solid #E5E7EB",
              }}
            >
              <Tabs
                value={infoTabValue}
                onChange={async (e, newValue) => {
                  setInfoTabValue(newValue);
                  // Fetch activities when switching to Activity tab if not already loaded
                  if (
                    newValue === 1 &&
                    infoFile?.id &&
                    activities.length === 0 &&
                    !isLoadingActivities
                  ) {
                    await fetchActivities(infoFile.id);
                  }
                }}
                sx={{
                  minHeight: 48,
                  "& .MuiTab-root": {
                    minHeight: 48,
                    fontSize: "14px",
                    textTransform: "none",
                    color: "#5F6368",
                    "&.Mui-selected": {
                      color: "#2932E5",
                    },
                  },
                  "& .MuiTabs-indicator": {
                    backgroundColor: "#2932E5",
                  },
                }}
              >
                <Tab label="Details" />
                <Tab label="Activity" />
              </Tabs>
            </Box>

            {/* Tab Content */}
            <Box
              sx={{
                flex: 1,
                overflow: "auto",
              }}
            >
              {infoTabValue === 0 /* Details Tab */ ? (
                <List
                  sx={{
                    py: 0,
                  }}
                >
                  <ListItem
                    sx={{
                      py: 2,
                      px: 3,
                      flexDirection: "column",
                      alignItems: "flex-start",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#5F6368",
                        fontSize: "11px",
                        textTransform: "uppercase",
                        letterSpacing: "0.8px",
                        mb: 0.5,
                      }}
                    >
                      TYPE
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "14px",
                        color: "#202124",
                      }}
                    >
                      {infoFile.type === "folder"
                        ? "Folder"
                        : infoFile.fileType || "File"}
                    </Typography>
                  </ListItem>

                  <Divider
                    sx={{
                      mx: 3,
                    }}
                  />

                  <ListItem
                    sx={{
                      py: 2,
                      px: 3,
                      flexDirection: "column",
                      alignItems: "flex-start",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#5F6368",
                        fontSize: "11px",
                        textTransform: "uppercase",
                        letterSpacing: "0.8px",
                        mb: 0.5,
                      }}
                    >
                      SIZE
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "14px",
                        color: "#202124",
                      }}
                    >
                      {infoFile.type === "folder"
                        ? formatFolderSize(infoFile)
                        : infoFile.size || "—"}
                    </Typography>
                  </ListItem>

                  <Divider
                    sx={{
                      mx: 3,
                    }}
                  />

                  {infoFile.type === "folder" && (
                    <>
                      <ListItem
                        sx={{
                          py: 2,
                          px: 3,
                          flexDirection: "column",
                          alignItems: "flex-start",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#5F6368",
                            fontSize: "11px",
                            textTransform: "uppercase",
                            letterSpacing: "0.8px",
                            mb: 0.5,
                          }}
                        >
                          ITEMS
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "14px",
                            color: "#202124",
                          }}
                        >
                          {getFolderItemCount(infoFile)}{" "}
                          {getFolderItemCount(infoFile) === 1
                            ? "item"
                            : "items"}
                        </Typography>
                      </ListItem>
                      <Divider
                        sx={{
                          mx: 3,
                        }}
                      />
                    </>
                  )}

                  <ListItem
                    sx={{
                      py: 2,
                      px: 3,
                      flexDirection: "column",
                      alignItems: "flex-start",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#5F6368",
                        fontSize: "11px",
                        textTransform: "uppercase",
                        letterSpacing: "0.8px",
                        mb: 0.5,
                      }}
                    >
                      OWNER
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mt: 0.5,
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 28,
                          height: 28,
                          bgcolor: "#2932E5",
                          fontSize: "13px",
                        }}
                      >
                        {(infoFile.owner || "M").charAt(0).toUpperCase()}
                      </Avatar>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "14px",
                          color: "#202124",
                        }}
                      >
                        {infoFile.owner || "Me"}
                      </Typography>
                    </Box>
                  </ListItem>

                  <Divider
                    sx={{
                      mx: 3,
                    }}
                  />

                  <ListItem
                    sx={{
                      py: 2,
                      px: 3,
                      flexDirection: "column",
                      alignItems: "flex-start",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#5F6368",
                        fontSize: "11px",
                        textTransform: "uppercase",
                        letterSpacing: "0.8px",
                        mb: 0.5,
                      }}
                    >
                      MODIFIED
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "14px",
                        color: "#202124",
                      }}
                    >
                      {formatDate(infoFile.dateModified) || "Today"} by{" "}
                      {infoFile.owner || "me"}
                    </Typography>
                  </ListItem>

                  <Divider
                    sx={{
                      mx: 3,
                    }}
                  />

                  <ListItem
                    sx={{
                      py: 2,
                      px: 3,
                      flexDirection: "column",
                      alignItems: "flex-start",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#5F6368",
                        fontSize: "11px",
                        textTransform: "uppercase",
                        letterSpacing: "0.8px",
                        mb: 0.5,
                      }}
                    >
                      CREATED
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "14px",
                        color: "#202124",
                      }}
                    >
                      {formatDate(infoFile.dateModified) || "Today"}
                    </Typography>
                  </ListItem>

                  <Divider
                    sx={{
                      mx: 3,
                    }}
                  />

                  <ListItem
                    sx={{
                      py: 2,
                      px: 3,
                      flexDirection: "column",
                      alignItems: "flex-start",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#5F6368",
                        fontSize: "11px",
                        textTransform: "uppercase",
                        letterSpacing: "0.8px",
                        mb: 0.5,
                      }}
                    >
                      LOCATION
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        mt: 0.5,
                      }}
                    >
                      <FolderSharedIcon
                        sx={{
                          fontSize: 16,
                          color: "#5F6368",
                        }}
                      />
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "14px",
                            color: "#2932E5",
                            cursor: "pointer",
                            "&:hover": {
                              textDecoration: "underline",
                            },
                          }}
                          onClick={() => handleBreadcrumbClick(-1)}
                        >
                          My Drawings
                        </Typography>
                        {breadcrumbs.length > 0 && (
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: "12px",
                              color: "#5F6368",
                              display: "block",
                            }}
                          >
                            {breadcrumbs.map((b) => b.name).join(" › ")}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </ListItem>

                  <Divider
                    sx={{
                      mx: 3,
                    }}
                  />

                  <ListItem
                    disabled={disableItem("description")}
                    sx={{
                      py: 2,
                      px: 3,
                      flexDirection: "column",
                      alignItems: "flex-start",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#5F6368",
                        fontSize: "11px",
                        textTransform: "uppercase",
                        letterSpacing: "0.8px",
                        mb: 1,
                      }}
                    >
                      DESCRIPTION
                    </Typography>
                    {fileDescription ? (
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "14px",
                          color: "#202124",
                          mb: 1,
                        }}
                      >
                        {fileDescription}
                      </Typography>
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "14px",
                          color: "#5F6368",
                          fontStyle: "italic",
                          mb: 1,
                        }}
                      >
                        Add a description
                      </Typography>
                    )}
                    <Button
                      disabled={disableItem("add description")}
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => {
                        const newDesc = prompt(
                          "Enter description:",
                          fileDescription
                        );
                        if (newDesc !== null) setFileDescription(newDesc);
                      }}
                      sx={{
                        fontSize: "13px",
                        textTransform: "none",
                      }}
                    >
                      {fileDescription ? "Edit" : "Add"} description
                    </Button>
                  </ListItem>
                </List> /* Activity Tab */
              ) : (
                <Box
                  sx={{
                    p: 0,
                    height: "100%",
                    overflow: "auto",
                  }}
                >
                  {isLoadingActivities ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        p: 4,
                      }}
                    >
                      <CircularProgress size={24} />
                    </Box>
                  ) : activities.length > 0 ? (
                    <List sx={{ p: 0 }}>
                      {activities.map((activity, index) => {
                        // Format activity data - handle different API response structures
                        const activityType =
                          activity.activity_type ||
                          activity.action ||
                          activity.type ||
                          "Activity";
                        // Capitalize first letter and format activity type
                        const activityAction =
                          activityType.charAt(0).toUpperCase() +
                          activityType.slice(1);
                        const activityUser =
                          activity.user_name ||
                          activity.username ||
                          activity.user ||
                          activity.created_by ||
                          infoFile.owner ||
                          "Unknown";
                        // Format timestamp
                        const formatTimestamp = (timestamp) => {
                          if (!timestamp) return "N/A";
                          try {
                            const date = new Date(timestamp);
                            return date.toLocaleString("en-US", {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            });
                          } catch (e) {
                            return timestamp;
                          }
                        };
                        const activityTimestamp = formatTimestamp(
                          activity.created_at ||
                            activity.timestamp ||
                            activity.date ||
                            activity.time
                        );
                        // Handle details - it can be an object or string
                        const getActivityDetails = () => {
                          if (!activity.details) return null;
                          if (typeof activity.details === "string") {
                            return activity.details;
                          }
                          if (typeof activity.details === "object") {
                            // Format details object
                            const parts = [];
                            if (activity.details.name)
                              parts.push(`Name: ${activity.details.name}`);
                            if (activity.details.type)
                              parts.push(`Type: ${activity.details.type}`);
                            if (activity.details.parent_id)
                              parts.push(
                                `Parent ID: ${activity.details.parent_id}`
                              );
                            return parts.length > 0 ? parts.join(", ") : null;
                          }
                          return (
                            activity.description || activity.message || null
                          );
                        };
                        const activityDetails = getActivityDetails();
                        const activityId = activity.id || activity._id || index;

                        return (
                          <Box key={activityId}>
                            <ListItem
                              sx={{
                                py: 2,
                                px: 3,
                                flexDirection: "column",
                                alignItems: "flex-start",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "start",
                                  gap: 2,
                                  width: "100%",
                                }}
                              >
                                <Avatar
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    bgcolor: "#2932E5",
                                    fontSize: "14px",
                                  }}
                                >
                                  {activityUser.charAt(0).toUpperCase()}
                                </Avatar>
                                <Box
                                  sx={{
                                    flex: 1,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontSize: "14px",
                                      fontWeight: 500,
                                      color: "#202124",
                                      mb: 0.5,
                                    }}
                                  >
                                    {activityAction}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontSize: "12px",
                                      color: "#5F6368",
                                      display: "block",
                                    }}
                                  >
                                    {activityUser} • {activityTimestamp}
                                  </Typography>
                                  {activityDetails && (
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontSize: "12px",
                                        color: "#5F6368",
                                        mt: 0.5,
                                        display: "block",
                                      }}
                                    >
                                      {activityDetails}
                                    </Typography>
                                  )}
                                </Box>
                                <HistoryIcon
                                  sx={{
                                    fontSize: 20,
                                    color: "#5F6368",
                                  }}
                                />
                              </Box>
                            </ListItem>
                            {index < activities.length - 1 && (
                              <Divider sx={{ mx: 3 }} />
                            )}
                          </Box>
                        );
                      })}
                    </List>
                  ) : (
                    <Box
                      sx={{
                        p: 3,
                        textAlign: "center",
                      }}
                    >
                      <HistoryIcon
                        sx={{
                          fontSize: 48,
                          color: "#E5E7EB",
                          mb: 1,
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#5F6368",
                        }}
                      >
                        No activity yet
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>

            {/* Bottom Action Buttons */}
            <Box
              sx={{
                p: 2,
                borderTop: "1px solid #E5E7EB",
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                }}
              >
                <Tooltip title={infoFile.starred ? "Remove star" : "Add star"}>
                  <IconButton
                    disabled={
                      disableItem("star") ||
                      displaySettings?.enableFavorites === false
                    }
                    size="small"
                    onClick={(e) => {
                      handleStarToggle(infoFile, e);
                      setInfoFile({
                        ...infoFile,
                        starred: !infoFile.starred,
                      });
                    }}
                    sx={{
                      border: "1px solid #E5E7EB",
                      borderRadius: 1,
                      color: infoFile.starred ? "#F4B400" : "#5F6368",
                    }}
                  >
                    {infoFile.starred ? (
                      <StarIcon fontSize="small" />
                    ) : (
                      <StarBorderIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Make a copy">
                  <IconButton
                    disabled={disableItem("make a copy")}
                    size="small"
                    onClick={() => toast.success("Copy created successfully")}
                    sx={{
                      border: "1px solid #E5E7EB",
                      borderRadius: 1,
                    }}
                  >
                    <FileCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Move to folder">
                  <IconButton
                    disabled={disableItem("move to")}
                    size="small"
                    onClick={() => toast("Select destination folder")}
                    sx={{
                      border: "1px solid #E5E7EB",
                      borderRadius: 1,
                    }}
                  >
                    <DriveFileMoveIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>
        )}
      </Drawer>

      {/* Fullscreen File Viewer */}
      <FullscreenFileViewer
        file={fullscreenFile}
        onClose={() => setFullscreenFile(null)}
        filesInFolder={displayedFiles.filter((f) => f.type === "file")}
        onNavigateToFile={(file) => {
          setFullscreenFile(file);
          if (onFileSelect) {
            onFileSelect(file);
          }
        }}
      />

      {/* Document Viewer for Office docs, videos, text files */}
      {viewerDocument && (
        <DocumentViewer
          open={showDocumentViewer}
          onClose={() => {
            setShowDocumentViewer(false);
            setViewerDocument(null);
          }}
          fileName={viewerDocument.fileName}
          fileUrl={viewerDocument.fileUrl}
          fileType={viewerDocument.fileType}
        />
      )}
    </Box>
  );
}
